import { getCourse, getCourses } from '../lib/courses.js';
import {
  getTopic, getTopics, updateTopicMeta, saveTopicVersion, fetchVersionHtml, listAvailableVersions,
  saveFailedAttempt, fetchFailedAttempt, clearFailedAttempt,
  saveVersionBeatPlan, fetchVersionBeatPlanRecord,
  watchGenerationJob, getGenerationJob, markTopicEmbeddingDirty,
} from '../lib/topics.js';
import { appendMessage, upsertAssistantMessage, getAssistantMessage, loadLiveMessages, loadArchivedMessages, clearChat } from '../lib/chat.js';
import {
  streamPluginAgentGeneration,
  checkPageCompleteness,
  stopAgentSession,
} from '../lib/generate.js';
import { currentUser, isAdminCached, canGenerateCached } from '../lib/auth.js';
import { navigate } from '../router.js';
import { injectAskAIBridge, attachAskAIBridge } from '../lib/askAI.js';
import { attachQuizScoreBridge } from '../lib/quizScore.js';
import { buildDynamicConversionDraft, buildDynamicConversionRecipe } from '../lib/dynamicRecipe.js';
import { injectElementPickerBridge, attachElementPickerBridge, armPicker, disarmPicker } from '../lib/elementPicker.js';
import { injectLayoutEditorBridge, attachLayoutEditorBridge } from '../lib/layoutEditor.js';
import { syncPublishedListingsForTopic } from '../lib/marketplace.js';
import { confirmDialog, alertDialog } from '../lib/dialog.js';
import { logClientError } from '../lib/errorLog.js';
import { createIdempotencyKey } from '../lib/idempotency.js';
import { RESPONSIVE_VIEWPORTS, summarizeResponsiveProblems, testRenderForErrors, testResponsiveHtml } from '../lib/sandbox.js';
import { friendlyError } from '../lib/friendlyError.js';
import { extractSection, resolvePinsToSection } from '../lib/sections.js';
import { checkViewingAccess, renderViewingGate } from '../lib/viewingAccess.js';

// Attached chat images (see the 📎 picker in the input row below) are sent
// to the model as vision input for that one turn only — never persisted to
// Firestore or Storage (chat history stays lightweight; see lib/chat.js).
// Must match functions/entitlement.js's MAX_IMAGE_BYTES so the client can
// reject an oversized file immediately instead of round-tripping to the
// server first.
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

// Rough client-side estimate of "this page is big enough that sending it
// whole for a refinement risks functions/entitlement.js's
// MAX_CURRENT_HTML_CHARS cap (300,000 chars — the "Current file is too
// large to send for refinement" error). Deliberately well below that hard
// cap, so there's room to react — by trying a section-targeted edit
// instead (see src/lib/sections.js) — before ever actually hitting it.
// Below this threshold, nothing changes: the whole file is sent exactly as
// it always was, no extra step for anyone, which matters most for simple
// pages (and the learners, including kids, who just want a quick change
// without doing anything special).
const SECTION_MODE_SUGGEST_THRESHOLD = 150000;

const GENERATION_BUSY_TEXT = [
  'blip blop... zzzzt...',
  'whirr-ka-doodle...',
  'mip mip... drawing sparks...',
  'floofing pixels...',
  'zorp zorp... almost something...',
  'tikka-takka thinking...',
  'wobble wobble rendering...',
  'plonk... shuffling ideas...',
];

const GENERATION_TEMPLATES = {
  auto: {
    marker: 'Auto',
    label: 'Auto',
    caption: 'AI decides',
    diagram: 'auto',
    brief: '',
  },
  sim: {
    marker: 'Sim',
    label: 'Sim',
    caption: 'Controls beside canvas',
    diagram: 'sim',
    brief: 'LAYOUT TEMPLATE MARKER: Sim. Use a simulator-first layout. Put the simulation viewport and every control/readout inside ONE shared card. On normal-width screens, place the simulation on the left and a compact control panel immediately on its RIGHT so the learner can operate controls and see their effects live. Never place controls in a separate card or above the simulation. On narrow phone screens only, the right-side panel may reflow directly below while remaining in the same card. Every control must visibly update the simulation and its readout immediately.',
  },
  qz: {
    marker: 'Qz',
    label: 'Qz',
    caption: 'Question then feedback',
    diagram: 'qz',
    brief: 'LAYOUT TEMPLATE MARKER: Qz. Use a quiz-style placement: question area first, answer controls immediately next to each question or directly beneath it, instant feedback close to the answer, and a compact score/progress strip that stays visible near the active question.',
  },
  ex: {
    marker: 'Ex',
    label: 'Ex',
    caption: 'Problem steps result',
    diagram: 'ex',
    brief: 'LAYOUT TEMPLATE MARKER: Ex. Use a worked-example placement: problem/context on the left or top, ordered steps in the main reading path, and result/check beside the steps. Keep any practice control or reveal button adjacent to the example it affects.',
  },
  dtl: {
    marker: 'Dtl',
    label: 'Dtl',
    caption: 'Overview sections recap',
    diagram: 'dtl',
    brief: 'LAYOUT TEMPLATE MARKER: Dtl. Use a detailed-explanation placement: overview at the top, structured detail sections below, diagrams or examples embedded next to the relevant explanation, and a recap/checkpoint at the end. Avoid separating controls from the visual they affect.',
  },
};

// Optional "look & feel" presets (point 9) — shown as a gallery tab in the
// setup modal so a learner can see roughly what visual style they're
// picking BEFORE generating anything, rather than only finding out after a
// full generation round-trip. Purely a style brief folded into the
// instruction sent to the model (see foldInLookFeelBrief) — it never
// constrains layout/content, only palette/typography/decoration language,
// and picking one is always optional: 'decide' ("Let website decide") is
// the default and folds in no extra brief at all, identical to today's
// behavior with no look & feel choice made.
const LOOK_FEEL_PRESETS = {
  decide: {
    label: 'Let website decide',
    caption: 'No preference — use whatever fits the topic best',
    brief: '',
    previewHtml: `<!doctype html><html><head><style>
      body{margin:0;font-family:system-ui,sans-serif;height:100%;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,#eef0f7,#f7f7fb);color:#444;}
      .msg{padding:14px 18px;border:1px dashed #c8cbe0;border-radius:10px;font-size:13px;text-align:center;}
    </style></head><body><div class="msg">🤖<br>Style picked automatically<br>based on the topic</div></body></html>`,
  },
  kid: {
    label: 'Playful & colorful',
    caption: 'Bright colors, rounded shapes, a little bounce — great for young learners',
    brief: 'LOOK & FEEL: Playful, colorful, kid-friendly visual style. Use bright, saturated but harmonious colors, big rounded corners, chunky friendly buttons, playful accent shapes (stars, blobs, soft squiggles), a slightly bouncy/springy feel on hover and interaction (small scale/wiggle micro-animations), large clear text, and simple friendly icons/emoji where appropriate. Keep it fun and inviting without sacrificing readability or the structural/layout rules above.',
    previewHtml: `<!doctype html><html><head><style>
      body{margin:0;font-family:'Comic Sans MS',system-ui,sans-serif;height:100%;background:linear-gradient(135deg,#ffd166,#ef476f 60%,#06d6a0);display:flex;align-items:center;justify-content:center;}
      .card{background:#fff;border-radius:24px;padding:16px 20px;box-shadow:0 8px 0 rgba(0,0,0,0.15);text-align:center;transform:rotate(-2deg);}
      .card button{background:#118ab2;color:#fff;border:none;border-radius:999px;padding:8px 18px;font-weight:800;font-size:13px;box-shadow:0 4px 0 #073b4c;}
      .dot{display:inline-block;width:10px;height:10px;border-radius:50%;margin:0 3px;}
    </style></head><body><div class="card">⭐ Let's learn! ⭐<br><br><button>Play</button><br><br>
      <span class="dot" style="background:#ef476f"></span><span class="dot" style="background:#ffd166"></span><span class="dot" style="background:#06d6a0"></span>
    </div></body></html>`,
  },
  mature: {
    label: 'Clean & professional',
    caption: 'Muted palette, sharp typography, no glossy decoration — for older / college students',
    brief: 'LOOK & FEEL: Clean, professional, non-glossy visual style suited to mature/college-level learners. Use a restrained, muted color palette (neutrals plus one understated accent), sharp/minimal corner radii, precise typographic hierarchy, generous whitespace, no cartoonish decoration, no bouncy/playful micro-animations, and subtle, purposeful motion only (a quick fade/slide, nothing springy). Favor a dense, information-forward layout over illustration while still respecting the structural/layout rules above.',
    previewHtml: `<!doctype html><html><head><style>
      body{margin:0;font-family:Georgia,'Times New Roman',serif;height:100%;background:#f4f4f2;display:flex;align-items:center;justify-content:center;}
      .card{background:#fff;border:1px solid #dcdcd6;padding:18px 22px;max-width:220px;}
      .card h4{margin:0 0 6px;font-size:14px;letter-spacing:0.02em;color:#1a1a1a;}
      .card p{margin:0 0 10px;font-size:12px;color:#555;line-height:1.4;}
      .card button{background:#1a1a1a;color:#fff;border:none;padding:6px 14px;font-size:12px;letter-spacing:0.03em;}
    </style></head><body><div class="card"><h4>Section 2.1 — Overview</h4><p>A precise, text-forward layout with restrained styling.</p><button>Continue</button></div></body></html>`,
  },
  dark: {
    label: 'Sleek dark mode',
    caption: 'Deep dark background with a glowing accent — focused, high-contrast, modern',
    brief: 'LOOK & FEEL: Sleek dark-mode visual style. Use a deep dark background (near-black or dark navy/charcoal), a single vivid accent color used sparingly for interactive elements and glow highlights, light gray/white text with strong contrast, modern sans-serif type, minimal flat surfaces with subtle glowing borders/shadows on interactive elements, and restrained, purposeful motion. Depth comes from glow and contrast, not decoration.',
    previewHtml: `<!doctype html><html><head><style>
      body{margin:0;font-family:system-ui,sans-serif;height:100%;background:#0b0d14;display:flex;align-items:center;justify-content:center;}
      .card{background:#14161f;border:1px solid #262b3a;border-radius:14px;padding:18px 22px;box-shadow:0 0 24px rgba(77,163,255,0.25);max-width:220px;}
      .card h4{margin:0 0 8px;font-size:14px;color:#e8eaf0;}
      .card p{margin:0 0 12px;font-size:12px;color:#8a92a6;line-height:1.4;}
      .card button{background:#4da3ff;color:#06111f;border:none;border-radius:8px;padding:7px 16px;font-size:12px;font-weight:700;box-shadow:0 0 14px rgba(77,163,255,0.6);}
    </style></head><body><div class="card"><h4>⚡ System status: online</h4><p>Glowing accents against deep dark surfaces.</p><button>Run simulation</button></div></body></html>`,
  },
  pastel: {
    label: 'Soft pastel',
    caption: 'Gentle pastel palette, soft rounded cards — calm and easy on the eyes',
    brief: 'LOOK & FEEL: Soft pastel visual style. Use a gentle, low-saturation pastel palette (soft pinks/blues/lavenders/mints), rounded soft-edged cards with light drop shadows, calm airy spacing, a friendly rounded sans-serif font, and only gentle, slow motion. Avoid harsh contrast or saturated colors — keep everything soft and soothing.',
    previewHtml: `<!doctype html><html><head><style>
      body{margin:0;font-family:'Segoe UI',system-ui,sans-serif;height:100%;background:linear-gradient(135deg,#fde2e4,#e2ece9 60%,#dfe7fd);display:flex;align-items:center;justify-content:center;}
      .card{background:#fffdfd;border-radius:20px;padding:16px 20px;box-shadow:0 6px 18px rgba(0,0,0,0.08);text-align:center;max-width:210px;}
      .card h4{margin:0 0 6px;font-size:14px;color:#5b5470;}
      .card p{margin:0 0 10px;font-size:12px;color:#8a8398;line-height:1.4;}
      .card button{background:#c9b6f0;color:#453a5c;border:none;border-radius:999px;padding:7px 16px;font-size:12px;font-weight:700;}
    </style></head><body><div class="card"><h4>🌸 Gentle & calm</h4><p>Soft pastels, rounded corners, quiet spacing.</p><button>Begin</button></div></body></html>`,
  },
  nature: {
    label: 'Nature & earthy',
    caption: 'Greens and warm browns, organic shapes — a natural, grounded feel',
    brief: 'LOOK & FEEL: Nature-inspired, earthy visual style. Use a palette of greens, warm browns, and muted earth tones, organic rounded shapes (leaf-like curves, soft blobs) instead of sharp geometric corners, a warm approachable font, and subtle natural gradients (e.g. soft green-to-cream) rather than flat corporate color blocks.',
    previewHtml: `<!doctype html><html><head><style>
      body{margin:0;font-family:'Trebuchet MS',system-ui,sans-serif;height:100%;background:linear-gradient(160deg,#eef4e6,#d9e8d1);display:flex;align-items:center;justify-content:center;}
      .card{background:#fbfaf4;border-radius:26px 12px 26px 12px;padding:16px 20px;box-shadow:0 6px 16px rgba(74,93,58,0.15);max-width:215px;}
      .card h4{margin:0 0 6px;font-size:14px;color:#3f5233;}
      .card p{margin:0 0 10px;font-size:12px;color:#6b7a5c;line-height:1.4;}
      .card button{background:#7a9a5a;color:#fff;border:none;border-radius:16px 6px 16px 6px;padding:7px 16px;font-size:12px;font-weight:700;}
    </style></head><body><div class="card"><h4>🌿 Grow &amp; explore</h4><p>Earthy tones, organic curves, grounded feel.</p><button>Explore</button></div></body></html>`,
  },
  corporate: {
    label: 'Corporate blue',
    caption: 'Navy and blue palette, structured and confident — a business/professional feel',
    brief: 'LOOK & FEEL: Corporate, business-professional visual style. Use a navy-and-blue palette on a crisp white background, structured card layouts with clear borders/dividers, a confident sans-serif font, precise alignment and grid structure, and minimal, purposeful use of a single accent blue for interactive elements. Avoid playful decoration.',
    previewHtml: `<!doctype html><html><head><style>
      body{margin:0;font-family:Arial,system-ui,sans-serif;height:100%;background:#fff;display:flex;align-items:center;justify-content:center;}
      .card{background:#fff;border:1px solid #d7dee8;border-left:4px solid #0b3d91;padding:16px 20px;max-width:220px;}
      .card h4{margin:0 0 6px;font-size:13px;color:#0b3d91;letter-spacing:0.02em;}
      .card p{margin:0 0 10px;font-size:12px;color:#3d4a5c;line-height:1.4;}
      .card button{background:#0b3d91;color:#fff;border:none;padding:7px 16px;font-size:12px;font-weight:600;}
    </style></head><body><div class="card"><h4>QUARTERLY OVERVIEW</h4><p>Structured, precise, and built for clarity.</p><button>View report</button></div></body></html>`,
  },
  sunset: {
    label: 'Warm sunset',
    caption: 'Oranges, pinks and purples in a glowing gradient — energetic and vibrant',
    brief: 'LOOK & FEEL: Warm, vibrant "sunset" visual style. Use a warm gradient palette moving through orange, coral/pink, and purple, glowing soft-edged shapes, a bold friendly sans-serif font, and confident saturated (but harmonious, not clashing) color blocks. Keep an energetic, optimistic feel throughout.',
    previewHtml: `<!doctype html><html><head><style>
      body{margin:0;font-family:'Segoe UI',system-ui,sans-serif;height:100%;background:linear-gradient(135deg,#ff9a56,#ff6b9d 55%,#a855f7);display:flex;align-items:center;justify-content:center;}
      .card{background:rgba(255,255,255,0.92);border-radius:18px;padding:16px 20px;box-shadow:0 8px 22px rgba(168,85,247,0.35);max-width:210px;}
      .card h4{margin:0 0 6px;font-size:14px;color:#7c2d6b;}
      .card p{margin:0 0 10px;font-size:12px;color:#8a5570;line-height:1.4;}
      .card button{background:linear-gradient(135deg,#ff6b9d,#a855f7);color:#fff;border:none;border-radius:999px;padding:7px 18px;font-size:12px;font-weight:800;}
    </style></head><body><div class="card"><h4>🔥 Bright &amp; bold</h4><p>Glowing gradients, vibrant energy.</p><button>Let's go</button></div></body></html>`,
  },
  mono: {
    label: 'Minimalist monochrome',
    caption: 'Grayscale plus one accent color, lots of whitespace — modern and understated',
    brief: 'LOOK & FEEL: Minimalist monochrome visual style. Use an almost-entirely grayscale palette (white/near-white background, black/charcoal text) plus exactly ONE accent color used sparingly for interactive elements only, generous whitespace, a modern geometric sans-serif font, sharp minimal corner radii, and no decorative color or illustration. Let structure and typography carry the design.',
    previewHtml: `<!doctype html><html><head><style>
      body{margin:0;font-family:'Helvetica Neue',Arial,sans-serif;height:100%;background:#fff;display:flex;align-items:center;justify-content:center;}
      .card{padding:20px 26px;max-width:210px;}
      .card h4{margin:0 0 10px;font-size:15px;color:#111;letter-spacing:-0.01em;}
      .card p{margin:0 0 14px;font-size:12px;color:#666;line-height:1.5;}
      .card button{background:#111;color:#fff;border:none;padding:7px 18px;font-size:11px;letter-spacing:0.06em;text-transform:uppercase;}
    </style></head><body><div class="card"><h4>Structure first</h4><p>One accent color. Everything else is space and type.</p><button>Continue</button></div></body></html>`,
  },
  retro: {
    label: 'Retro terminal',
    caption: 'Monospace glowing text on a dark screen — a classic computer-terminal feel',
    brief: 'LOOK & FEEL: Retro computer-terminal visual style. Use a black or very dark background, a monospace font throughout, glowing green or amber text (pick one phosphor color and use it consistently), simple scanline or ASCII-border decorative touches, sharp (not rounded) corners, and a slightly nostalgic "old CRT terminal" feel while keeping body text legible.',
    previewHtml: `<!doctype html><html><head><style>
      body{margin:0;font-family:'Courier New',monospace;height:100%;background:#03110a;display:flex;align-items:center;justify-content:center;}
      .card{border:1px solid #1fbf5c;padding:14px 18px;max-width:220px;color:#3ef07f;text-shadow:0 0 6px rgba(62,240,127,0.7);}
      .card h4{margin:0 0 8px;font-size:13px;}
      .card p{margin:0 0 10px;font-size:11px;line-height:1.5;opacity:0.85;}
      .card button{background:transparent;border:1px solid #3ef07f;color:#3ef07f;padding:5px 14px;font-family:inherit;font-size:11px;}
    </style></head><body><div class="card"><h4>&gt; SYSTEM READY_</h4><p>run simulation.exe<br>loading modules... done</p><button>[ EXECUTE ]</button></div></body></html>`,
  },
  notebook: {
    label: 'Handwritten notebook',
    caption: 'Cream paper background, handwriting-style headings — feels like real class notes',
    brief: 'LOOK & FEEL: Handwritten-notebook visual style. Use a soft cream/paper-like background, a casual handwriting-style display font for headings and labels while keeping body text in a legible sans/serif, warm ink-like accent colors (pen blue, pen red, highlighter yellow), and small doodle-like accents (underlines, circles, arrows) rather than glossy UI decoration.',
    previewHtml: `<!doctype html><html><head><style>
      body{margin:0;font-family:'Bradley Hand','Comic Sans MS',cursive;height:100%;background:#fbf6e9;display:flex;align-items:center;justify-content:center;}
      .card{background:#fffdf6;border:1px solid #e4dbc0;padding:16px 20px;max-width:220px;box-shadow:2px 3px 0 rgba(0,0,0,0.06);}
      .card h4{margin:0 0 8px;font-size:16px;color:#2c4e8a;text-decoration:underline wavy #e0b93c;}
      .card p{margin:0 0 10px;font-size:13px;color:#333;font-family:Georgia,serif;line-height:1.4;}
      .card button{background:#fff;border:2px solid #c0392b;color:#c0392b;border-radius:50%;padding:8px 14px;font-family:inherit;font-size:12px;}
    </style></head><body><div class="card"><h4>Today's notes ✎</h4><p>Feels just like a real notebook page.</p><button>Next</button></div></body></html>`,
  },
};

// Three finer-grained style controls, independent of (and additive with) the
// LOOK_FEEL_PRESETS gallery above — picking a whole preset still works on its
// own, but a learner can also just nudge the font, text size, or background
// color without committing to a full theme. Each folds one more line into
// the same brief (see foldInLookFeelBrief); 'decide' folds in nothing,
// identical to not touching this control at all.
const FONT_PRESETS = {
  decide: { label: 'Let AI choose', brief: '' },
  sans: { label: 'Modern sans-serif', brief: 'FONT PREFERENCE: Use a clean modern sans-serif font family (e.g. system-ui/Inter/Segoe UI/Helvetica-style) throughout the page.' },
  serif: { label: 'Classic serif', brief: 'FONT PREFERENCE: Use a classic serif font family (e.g. Georgia/Times New Roman-style) for a traditional, book-like reading feel.' },
  rounded: { label: 'Rounded & friendly', brief: 'FONT PREFERENCE: Use a rounded, friendly-feeling font family with soft, approachable letterforms, suited to a younger audience.' },
  mono: { label: 'Monospace / technical', brief: 'FONT PREFERENCE: Use a monospace/technical font family (e.g. Consolas/Menlo/Courier-style) for a code/technical feel.' },
  handwritten: { label: 'Handwritten / casual', brief: 'FONT PREFERENCE: Use a casual handwriting-style display font for headings, keeping body text in a legible sans/serif font for readability.' },
};
const FONT_SIZE_PRESETS = {
  decide: { label: 'Standard', brief: '' },
  large: { label: 'Large text', brief: 'TEXT SIZE PREFERENCE: Use larger-than-default base font sizes throughout (roughly 15-20% bigger body text and headings) for easier reading.' },
  compact: { label: 'Compact', brief: 'TEXT SIZE PREFERENCE: Use a more compact, information-dense type scale (smaller base font size, tighter line-height) to fit more content on screen.' },
};
const BG_COLOR_PRESETS = {
  decide: { label: 'Let AI choose', swatch: null, brief: '' },
  white: { label: 'White', swatch: '#ffffff', brief: 'BACKGROUND COLOR PREFERENCE: Use a clean white main page background, with dark, accessible text.' },
  cream: { label: 'Soft cream', swatch: '#faf3e7', brief: 'BACKGROUND COLOR PREFERENCE: Use a soft warm cream/off-white main page background, with dark, accessible text.' },
  lightgray: { label: 'Light gray', swatch: '#eef1f5', brief: 'BACKGROUND COLOR PREFERENCE: Use a light neutral gray main page background, with dark, accessible text.' },
  navy: { label: 'Deep navy', swatch: '#0f1b33', brief: 'BACKGROUND COLOR PREFERENCE: Use a deep navy/dark main page background, with light, accessible text and colors chosen for good contrast on a dark background.' },
  sage: { label: 'Soft sage', swatch: '#e4ede4', brief: 'BACKGROUND COLOR PREFERENCE: Use a soft muted sage-green main page background, with dark, accessible text.' },
};

function fontChoiceRowHtml(presets, dataAttr, extraClass) {
  return Object.entries(presets).map(([id, p]) => `<button type="button" class="btn btn-sm ${extraClass}" data-${dataAttr}="${id}" title="${escapeHtml(p.label)}">${escapeHtml(p.label)}</button>`).join('');
}
function bgColorChoiceRowHtml() {
  return Object.entries(BG_COLOR_PRESETS).map(([id, p]) => `
    <button type="button" class="bgcolor-choice" data-bgcolor-id="${id}" title="${escapeHtml(p.label)}">
      <span class="bgcolor-swatch" style="background:${p.swatch || 'conic-gradient(from 0deg, #ff6b9d, #ffd166, #06d6a0, #4da3ff, #a855f7, #ff6b9d)'}"></span>
      <span class="bgcolor-choice-label">${escapeHtml(p.label)}</span>
    </button>`).join('');
}

function lookFeelCardHtml(id, preset) {
  return `
    <button type="button" class="lookfeel-choice ${id === 'decide' ? 'is-active' : ''}" data-lookfeel-id="${id}" title="${escapeHtml(preset.caption)}">
      <span class="lookfeel-choice-preview"><iframe tabindex="-1" sandbox="" srcdoc="${escapeHtml(preset.previewHtml)}"></iframe></span>
      <span class="lookfeel-choice-label">${escapeHtml(preset.label)}</span>
    </button>
  `;
}

function templateButtonHtml(id, template) {
  return `
    <button type="button" class="template-choice ${id === 'auto' ? 'is-active' : ''}" data-template-id="${id}" title="${template.caption}">
      <span class="template-choice-code">${template.marker}</span>
      <span class="template-choice-diagram template-diagram-${template.diagram}" aria-hidden="true">${templateDiagramHtml(template.diagram)}</span>
      <span class="template-choice-caption">${template.caption}</span>
    </button>
  `;
}

function templateDiagramHtml(type) {
  if (type === 'sim') {
    return '<i class="td-visual"></i><i class="td-controls"></i><i class="td-readout"></i>';
  }
  if (type === 'qz') {
    return '<i class="td-question"></i><i class="td-answer"></i><i class="td-feedback"></i>';
  }
  if (type === 'ex') {
    return '<i class="td-problem"></i><i class="td-steps"></i><i class="td-result"></i>';
  }
  if (type === 'dtl') {
    return '<i class="td-head"></i><i class="td-section-a"></i><i class="td-section-b"></i><i class="td-recap"></i>';
  }
  return '<i class="td-auto-a"></i><i class="td-auto-b"></i><i class="td-auto-c"></i>';
}

export async function renderStudio(container, { courseId, topicId }) {
  const uid = currentUser().uid;
  const [course, topic] = await Promise.all([getCourse(uid, courseId), getTopic(uid, courseId, topicId)]);
  if (!course || !topic) {
    container.innerHTML = `<div class="empty-state"><h3>Not found</h3><a href="#/">Back home</a></div>`;
    return;
  }

  // Viewing gate (₹/month, separate from generation) — see
  // lib/viewingAccess.js. Studio's live preview is exactly the "viewing"
  // that gate is meant to cover, and until now it had no check at all:
  // views/course.js's plain topic view (renderDetail) correctly blocked a
  // lapsed account, but the SAME topic opened here in Studio rendered fine
  // regardless — a real bypass for anyone whose subscription lapsed (not
  // just admins, who are separately exempt server-side — see
  // functions/wallet.js's ensureViewingAccess). Only gates topics that
  // already have saved content; a brand-new topic has nothing to view yet,
  // so there's nothing to block (matches renderDetail's own `if
  // (topic.currentVersion)` gate).
  if (topic.currentVersion) {
    const access = await checkViewingAccess();
    if (access.blocked) {
      container.innerHTML = `
        <div style="padding:24px;">
          <a href="#/course/${courseId}" style="display:inline-block;margin-bottom:16px;">&larr; Back to course</a>
          <div id="studio-viewing-gate"></div>
        </div>
      `;
      renderViewingGate(container.querySelector('#studio-viewing-gate'), access, () => renderStudio(container, { courseId, topicId }));
      return;
    }
  }

  let loadError = null;
  let currentHtml = null;
  if (topic.currentVersion) {
    try {
      currentHtml = await fetchVersionHtml(uid, courseId, topicId, topic.currentVersion);
    } catch (err) {
      loadError = friendlyError(err, 'Could not load this content.');
    }
  }

  // "Reference existing content" (see views/course.js openTopicModal and
  // lib/topics.js createTopic's referenceTopicId): loaded once, up front,
  // ONLY for a brand-new topic (no currentVersion yet) that was created
  // pointing at another topic. It's handed to streamPluginAgentGeneration
  // for the FIRST send only (see handleSendContinued below) — once this
  // topic has its own currentHtml, pluginAgentHandler.js ignores
  // referenceHtml anyway, so nothing needs to explicitly clear this
  // afterward. Best-
  // effort: if the referenced topic was since deleted/emptied, this just
  // silently stays null and the first generation behaves exactly like any
  // other brand-new topic.
  let referenceHtml = null;
  let referenceTopicLabel = null;
  // referenceMode ('style' | 'source') always comes from the New Topic
  // dialog's explicit picker (views/course.js) — never guessed here from
  // the two titles. Defaults to 'style' for topics created before this
  // field existed.
  const referenceMode = topic.referenceMode === 'source' ? 'source' : 'style';
  if (!topic.currentVersion && topic.referenceTopicId && topic.referenceCourseId) {
    try {
      const refTopic = await getTopic(uid, topic.referenceCourseId, topic.referenceTopicId);
      if (refTopic && refTopic.currentVersion) {
        referenceHtml = await fetchVersionHtml(uid, topic.referenceCourseId, topic.referenceTopicId, refTopic.currentVersion);
        referenceTopicLabel = topic.referenceTopicTitle || refTopic.title || 'that topic';
      }
    } catch (_) { /* fail-open — first generation just proceeds without a reference */ }
  }

  // Set (server-side only — see functions/topicPersistence.js) while a
  // generation for this topic is in flight, regardless of which tab (if
  // any) started it. Captured once, up front, before any of this render's
  // own event handlers can touch it — see watchActiveJob further down.
  const initialActiveJobId = topic.activeGenerationJobId || null;

  let viewingVersion = topic.currentVersion || null;
  let isGenerating = false;
  let activeAgentSessionId = null;
  let stopRequested = false;
  let archiveLoaded = false;
  // Whatever the model produced on the most recent FAILED attempt, kept
  // both in memory and durably in Storage (see lib/topics.js) so
  // "Regenerate" can ask the model to fix/finish this specific output
  // instead of starting over — and so it survives a reload even if the
  // user never clicks anything.
  let lastFailedRawHtml = null;
  // The image currently attached in the composer, not yet sent:
  // { dataUrl, name, sizeLabel } or null.
  let pendingImage = null;
  // Another topic's page, picked via the 🔗 button in the composer (see
  // openReferencePicker below), attached for THIS one message only — not
  // persisted on the topic doc like referenceTopicId/referenceMode above
  // (that pair only ever fires once, on a brand-new topic's first
  // generation). This is the "any time, per-message" counterpart: pick a
  // topic + mode fresh for a mid-conversation refinement too.
  // { courseId, topicId, title, mode, html } or null.
  let pendingReference = null;
  // Ask-AI bridge (see lib/askAI.js) for whichever iframe is currently in
  // the preview pane — re-attached every time the preview is replaced
  // (new generation, version switch), torn down first so listeners don't
  // pile up across a long studio session.
  let detachAskAI = null;
  // Quiz-score bridge (see lib/quizScore.js) always accompanies the Ask-AI
  // bridge 1:1, so it's torn down alongside rather than tracked separately.
  let detachQuizScore = null;
  function stopAskAIBridge() {
    detachAskAI?.();
    detachAskAI = null;
    detachQuizScore?.();
    detachQuizScore = null;
  }
  // Element comment-picker (see lib/elementPicker.js) — same reattach-on-
  // every-preview-swap treatment as the Ask-AI bridge above. pickModeOn
  // persists across regenerations/version switches (re-armed on the new
  // iframe automatically) so the learner doesn't have to re-toggle it
  // after every message; pendingPins does NOT persist — pins refer to
  // exact positions in the CURRENT rendered HTML, so they're cleared
  // whenever that HTML changes (see updatePreview).
  let detachPicker = null;
  let pickModeOn = false;
  let pendingPins = [];
  // Shown at most once per topic-open, the first time a send would benefit
  // from pinning a section instead of sending the whole (large) file — see
  // SECTION_MODE_SUGGEST_THRESHOLD and handleSend below.
  let oversizedTipShown = false;
  // Shown at most once per topic-open, the first time a coding-agent send
  // looks like it bundles several distinct asks — see
  // looksLikeMultiPartAgentRequest and handleSend below.
  let multiPartNudgeShown = false;
  let layoutEditor = null;
  let layoutEditing = false;
  function stopPickerBridge() {
    disarmPicker(container.querySelector('#preview-frame'));
    detachPicker?.();
    detachPicker = null;
  }
  function stopLayoutEditor() {
    layoutEditor?.detach();
    layoutEditor = null;
  }

  // A device preview changes the iframe's actual viewport dimensions. It is
  // intentionally not CSS zoom: media queries, ResizeObserver, canvas size,
  // and touch layouts must receive the same dimensions a real device would.
  const previewViewportOptions = [
    { id: 'fit', label: 'Fit workspace', width: null, height: null },
    ...RESPONSIVE_VIEWPORTS,
    { id: 'custom', label: 'Custom size', width: 390, height: 844 },
  ];
  let selectedPreviewViewportId = 'fit';
  let customPreviewWidth = 390;
  let customPreviewHeight = 844;
  const previewFrameMarkup = () => `
    <div id="responsive-preview-stage" class="responsive-preview-stage">
      <div id="responsive-preview-device" class="responsive-preview-device is-fit">
        <iframe id="preview-frame" sandbox="allow-scripts" allowfullscreen allow="fullscreen"></iframe>
      </div>
    </div>`;
  const previewViewportOptionsHtml = previewViewportOptions.map((viewport) =>
    `<option value="${viewport.id}">${escapeHtml(viewport.label)}</option>`
  ).join('');

  container.innerHTML = `
    <div class="studio">
      <div class="studio-topbar">
        <div style="display:flex;align-items:center;gap:10px;min-width:0;flex:1 1 auto;">
          <button class="btn btn-sm" id="back-btn" style="flex-shrink:0;">← ${escapeHtml(course.title)}</button>
          <strong id="topic-title-display" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1 1 auto;min-width:0;">${escapeHtml(topic.title)}</strong>
          <span id="dynamic-mode-slot" style="flex-shrink:0;">${topic.contentMode === 'dynamic' ? '<span class="pill" title="This topic is meant to call the built-in AI bridge (window.askAI) at runtime rather than being fully static">🧠 Dynamic</span>' : '<button type="button" class="btn btn-sm btn-ghost" id="make-dynamic-btn" title="Wire this page up to fetch fresh content from the AI at runtime instead of having it fixed">🧠 Make dynamic</button>'}</span>
        </div>
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;flex-shrink:0;">
          <span id="stream-status"></span>
          <select id="version-select" aria-label="Saved versions" title="Saved versions (latest five)" ${topic.currentVersion ? '' : 'disabled'}></select>
          <span id="version-loading" class="version-loading" role="status" aria-live="polite" style="display:none;">
            <span class="version-loading-spinner" aria-hidden="true"></span>
            Loading…
          </span>
          <button class="btn btn-sm" id="restore-btn" style="display:none;">Restore this version</button>
          <button class="btn btn-sm" id="view-spec-btn" style="display:none;" title="The component specification this version was built from">📋 Spec</button>
          <button class="btn btn-sm" id="toggle-chat-btn" title="Hide the chat side and show only the webpage">👁 Preview only</button>
          <button class="btn btn-sm" id="fullscreen-btn" ${currentHtml ? '' : 'disabled'}>⛶ Fullscreen</button>
          <button class="btn btn-sm" id="download-html-btn" style="display:none;" ${currentHtml ? '' : 'disabled'} title="Download the generated page as an .html file (admin only)">⬇ Download HTML</button>
        </div>
      </div>
      <div class="studio-body" id="studio-body">
        <div class="chat-pane" id="chat-pane" style="width:38%;">
          <div style="display:flex;justify-content:space-between;align-items:center;padding:10px 14px;border-bottom:1px solid var(--border);">
            <span style="font-size:0.85rem;color:var(--text-dim);">Chat</span>
            <button class="btn btn-sm btn-ghost" id="clear-chat-btn">Clear chat</button>
          </div>
          <div id="recovery-banner" style="display:none;padding:10px 14px;background:var(--surface-2);border-bottom:1px solid var(--border);font-size:0.85rem;"></div>
          <div id="kit-gap-notice-banner" style="display:none;padding:10px 14px;background:var(--accent-soft);border-bottom:1px solid var(--border);font-size:0.85rem;"></div>
          <div class="chat-messages" id="chat-messages">
            <button class="btn btn-sm" id="load-older-btn" style="align-self:center;">Load older messages</button>
          </div>
          <div id="image-preview-row" class="image-preview-row" style="display:none;"></div>
          <div id="reference-preview-row" class="image-preview-row" style="display:none;"></div>
          <div id="pin-preview-row" class="image-preview-row" style="display:none;"></div>
          <div class="chat-input-row">
            <input type="file" id="chat-image-input" accept="image/*" style="display:none;">
            <textarea id="chat-input" rows="2" placeholder="${topic.currentVersion ? 'Describe a change, e.g. make the circle bigger' : 'Describe what to build, e.g. an interactive page teaching circle theorems'}"></textarea>
            <div class="chat-input-actions">
              <button class="btn btn-sm btn-ghost" id="attach-image-btn" type="button" title="Attach an image (max 5 MB) for the AI to see">📎</button>
              <button class="btn btn-sm btn-ghost" id="attach-reference-btn" type="button" title="Reference another topic you've built for this one message">🔗</button>
              <button class="btn btn-sm btn-ghost" id="open-setup-btn" type="button" title="Choose a layout template, animation style, and look &amp; feel for this topic">⚙ Setup</button>
              <button class="btn btn-primary" id="send-btn">Send</button>
            </div>
          </div>
        </div>
        <div class="splitter" id="splitter"></div>
        <div class="preview-pane" id="preview-pane">
          <div class="preview-toolbar">
            <span style="font-size:0.85rem;color:var(--text-dim);">Live preview (sandboxed)</span>
            <div class="preview-toolbar-actions">
              <div class="responsive-preview-controls" aria-label="Preview device size">
                <label for="preview-viewport-select">Screen</label>
                <select id="preview-viewport-select" title="Render this same lesson at a real device viewport" ${currentHtml ? '' : 'disabled'}>${previewViewportOptionsHtml}</select>
                <label class="sr-only" for="preview-width-input">Preview width</label>
                <input id="preview-width-input" type="number" inputmode="numeric" min="240" max="2560" value="390" title="Custom viewport width in CSS pixels" disabled>
                <span aria-hidden="true">×</span>
                <label class="sr-only" for="preview-height-input">Preview height</label>
                <input id="preview-height-input" type="number" inputmode="numeric" min="320" max="2560" value="844" title="Custom viewport height in CSS pixels" disabled>
                <label class="sr-only" for="preview-width-slider">Drag custom preview width</label>
                <input id="preview-width-slider" class="preview-width-slider" type="range" min="240" max="1440" value="390" title="Drag to change custom preview width" disabled>
              </div>
              <span id="responsive-preview-status" class="layout-editor-status" role="status" aria-live="polite"></span>
              <button class="btn btn-sm" id="check-responsive-btn" ${currentHtml ? '' : 'disabled'} title="Test this lesson at every supported phone, tablet, and laptop viewport">Check responsive</button>
              <span id="layout-editor-status" class="layout-editor-status" style="display:none;"></span>
              <div id="layout-tools" class="layout-tools" style="display:none;">
                <button class="btn btn-sm" type="button" data-layout-action="undo" title="Undo placement" disabled>↶</button>
                <button class="btn btn-sm" type="button" data-layout-action="redo" title="Redo placement" disabled>↷</button>
                <button class="btn btn-sm" type="button" data-layout-action="reset" title="Reset changes made to this part in this editing session">Reset</button>
                <button class="btn btn-sm" type="button" data-layout-action="choose" title="Pick a different card or section to work on">⇄ Choose a different part</button>
              </div>
              <button class="btn btn-sm" id="cancel-layout-btn" style="display:none;">Cancel placement</button>
              <button class="btn btn-sm btn-primary" id="finalize-layout-btn" style="display:none;">Finalize layout</button>
              <button class="btn btn-sm" id="rearrange-btn" ${currentHtml ? '' : 'disabled'} title="Pick a card or section, then drag its parts into place">↕ Rearrange parts</button>
              <button class="btn btn-sm" id="pick-element-btn" ${currentHtml ? '' : 'disabled'}
                title="Click something in the page below, then describe what should change about it">
                💬 Comment on something
              </button>
              <button class="btn btn-sm" id="check-completeness-btn" ${currentHtml ? '' : 'disabled'}
                title="Re-check this page against what you asked for, using a stronger model — for the gaps that aren't obvious just from looking">
                ✓ Check completeness
              </button>
            </div>
          </div>
          <div id="preview-area" style="flex:1;position:relative;">
            <div id="preview-frame-slot" style="position:absolute;inset:0;">
              ${currentHtml
                ? previewFrameMarkup()
                : loadError
                  ? `<div class="empty-state">Could not load the saved version: ${escapeHtml(loadError)}</div>`
                  : `<div class="empty-state">Send a message to generate this topic's first version.${referenceHtml ? `<br/><span style="font-size:0.82rem;opacity:0.8;">📎 Referencing "${escapeHtml(referenceTopicLabel || 'that topic')}" ${referenceMode === 'source' ? 'as the source content to build from' : "for quality and layout — its subject won't carry over"}.</span>` : ''}</div>`}
            </div>
            <div id="pin-markers-layer" style="position:absolute;inset:0;pointer-events:none;"></div>
            <!-- Shown/hidden entirely by setStreaming() (display:none by
                 default here) — this is the "loud, hard-to-miss" in-flight
                 signal .generating-overlay's own CSS comment describes.
                 Coding-agent runs live exactly as long as this tab: no
                 durable session, no background continuation, no Stop/
                 interject controls (see functions/agentSessionSweep.js's
                 header). Closing the tab ends the run; reopen the topic
                 and resend the message to generate again. -->
            <div id="generating-overlay" class="generating-overlay" style="display:none;">
              <div class="generating-spinner"></div>
              <div class="generating-title">Generating…</div>
              <div id="generating-estimate" class="generating-estimate" style="display:none;"></div>
              <div id="generating-sub" class="generating-sub"></div>
              <button type="button" class="btn btn-danger" id="stop-generation-btn" disabled>Stop</button>
              <div class="generating-progress-track"><div class="generating-progress-fill"></div></div>
            </div>
            </div>
            <button type="button" id="agent-activity-show-btn" class="btn btn-sm" style="display:none;">🛠️ Agent activity</button>
            <div id="agent-activity-panel" class="agent-activity-panel">
              <div class="agent-activity-header">
                <span>🛠️ Coding agent activity</span>
                <button type="button" id="agent-activity-hide-btn" class="btn btn-sm">Hide</button>
              </div>
              <div id="agent-activity-log" class="agent-activity-log"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;

  const chatMessagesEl = container.querySelector('#chat-messages');
  const previewArea = container.querySelector('#preview-area');
  const versionSelect = container.querySelector('#version-select');
  const versionLoading = container.querySelector('#version-loading');
  const streamStatus = container.querySelector('#stream-status');
  const restoreBtn = container.querySelector('#restore-btn');
  const recoveryBanner = container.querySelector('#recovery-banner');
  const kitGapNoticeBanner = container.querySelector('#kit-gap-notice-banner');
  const imagePreviewRow = container.querySelector('#image-preview-row');
  const referencePreviewRow = container.querySelector('#reference-preview-row');
  const agentActivityPanel = container.querySelector('#agent-activity-panel');
  const agentActivityLog = container.querySelector('#agent-activity-log');
  const agentActivityShowBtn = container.querySelector('#agent-activity-show-btn');
  const agentActivityHideBtn = container.querySelector('#agent-activity-hide-btn');
  const downloadHtmlBtn = container.querySelector('#download-html-btn');
  // Re-seed from whatever was actually baked into the topic's last saved
  // version (functions/topicPersistence.js's saveGeneratedVersion now
  // persists this) instead of always starting at the 'auto'/'decide'
  // defaults. Without this, reopening an existing topic in a fresh tab and
  // sending ANY edit re-sent 'decide' to the server, which silently
  // stripped out a background/template the learner had explicitly chosen
  // in an earlier session — the page then adapted to the visitor's own
  // OS light/dark setting instead of staying on the chosen look, with no
  // action from the learner. See feedback thread for the incident this
  // fixes: a topic's theme changing "on its own" between sessions.
  let selectedTemplateId = GENERATION_TEMPLATES[topic.templateId] ? topic.templateId : 'auto';
  // Learner's per-request choice between one continuous camera view and
  // distinct per-scene camera shots (functions/systemPrompt.js reads this
  // to instruct 3D generations accordingly; functions/libs/simKit3D.js's
  // CameraDirector is what a 'cinematic' page actually uses at runtime).
  // Persists across sends within this topic session, same as selectedTemplateId.
  let pendingStagingMode = 'continuous';

  // The plugin-agent pipeline (functions/pluginAgentHandler.js's
  // generatePluginAgent) is the only generation pipeline — the single-shot
  // and beat-by-beat pipelines this comment used to describe (and the
  // "Detail level"/"Pipeline" tabs that used to choose between them) were
  // removed along with functions/generateHandler.js, and later the original
  // coding-agent pipeline (generateHtmlAgent) was retired from this flow
  // too. isAdminUser below now only gates admin-only affordances that are
  // still real: the live Agent Activity panel, and the raw-HTML download
  // button.
  const isAdminUser = isAdminCached();
  // Agent Activity toggle: visible for an admin for the whole time this view
  // is open, not just while a coding-agent generation is actively streaming.
  // Previously this only flipped on inside runGeneration's useCodingAgent
  // branch (a few hundred lines down) and flipped back off after any
  // non-agent run, so the only way to open the panel was to kick off a brand
  // new agent generation in THIS page session — reviewing an earlier run's
  // log, or just checking the panel exists, had no path in. The panel itself
  // is still populated live only (resetAgentActivityLog runs at the start of
  // each agent generation) — this just controls whether the button to open
  // it is there at all.
  if (agentActivityShowBtn && isAdminUser) agentActivityShowBtn.style.display = '';
  // Belt-and-braces: the panel only ever gets its "is-open" class added from
  // an admin-gated call site (openAgentActivityPanel(), only ever invoked by
  // this button's click handler and by showAgentPanel below — both already
  // isAdminUser-gated), so a non-admin should never see it open. Removing
  // the node's display outright for a non-admin here means that stays true
  // even if a future change adds another call site and forgets the gate —
  // there is no display value that CSS's `.is-open` class could then turn
  // back on for someone this line has already hidden.
  if (agentActivityPanel && !isAdminUser) agentActivityPanel.style.display = 'none';
  // Downloading the generated HTML as a file is an admin-only affordance —
  // ordinary users/learners view pages in the app, they don't get a raw
  // .html export. Same cosmetic client-side gate as the rest of this view;
  // there is nothing server-side to bypass since the HTML already lives in
  // this page's own DOM/closure for the signed-in user viewing it.
  if (downloadHtmlBtn && isAdminUser) downloadHtmlBtn.style.display = '';
  // The plugin-agent pipeline (functions/pluginAgentHandler.js's
  // generatePluginAgent) is the only generation pipeline every send goes
  // through — the single-shot and beat-by-beat routes (generateWithValidation,
  // and the Detail level/Pipeline (admin) tabs that used to choose between
  // all three), and later the original coding-agent pipeline
  // (generateHtmlAgent) itself, were removed/retired from this flow, along
  // with the admin-only toggle that used to switch between the two agent
  // pipelines. `pendingPipelineMode` stays a fixed constant rather than
  // being deleted outright because the "bundled request" nudge below still
  // reads it (`pendingPipelineMode === 'agent'`) — removing the variable
  // would just mean hardcoding that same check to `true` there instead.
  const pendingPipelineMode = 'agent';
  // Optional "look & feel" preset (point 9) — see LOOK_FEEL_PRESETS above.
  // 'decide' ("Let website decide") is the default and folds in no brief at
  // all. Persists across sends within this topic session, same as the two above.
  let selectedLookFeelId = 'decide';
  // Finer-grained style overrides, independent of the preset above — see
  // FONT_PRESETS/FONT_SIZE_PRESETS/BG_COLOR_PRESETS. Same 'decide' = no
  // brief folded in, same session-only persistence.
  let selectedFontId = 'decide';
  let selectedFontSizeId = 'decide';
  // Re-seeded from the topic's persisted choice — see the matching comment
  // on selectedTemplateId above; this is the field the actual "theme
  // changed on its own" reports trace back to, since
  // functions/baseStyles.js's injectBaseStyles rebuilds the CSS override
  // block from scratch on every save using whatever value is sent, rather
  // than preserving what was already baked into the page.
  let selectedBgColorId = BG_COLOR_PRESETS[topic.bgColorId] ? topic.bgColorId : 'decide';

  // The initial iframe markup above has no srcdoc — set it now so
  // reopening a topic that already has a saved version actually shows it
  // instead of a blank preview until the next generation.
  if (currentHtml) {
    const initialFrame = container.querySelector('#preview-frame');
    if (initialFrame) {
      initialFrame.srcdoc = injectElementPickerBridge(injectAskAIBridge(currentHtml, { enhanceCanvas: false }));
      detachAskAI = attachAskAIBridge(initialFrame, {
        courseTitle: course.title, topicTitle: topic.title, courseId, topicId, ownerUid: uid,
      });
      detachQuizScore = attachQuizScoreBridge(initialFrame, { courseId, topicId });
      detachPicker = attachElementPickerBridge(initialFrame, { onPick: handleElementPick });
    }
  }

  const previewViewportSelect = container.querySelector('#preview-viewport-select');
  const previewWidthInput = container.querySelector('#preview-width-input');
  const previewHeightInput = container.querySelector('#preview-height-input');
  const previewWidthSlider = container.querySelector('#preview-width-slider');
  const responsivePreviewStatus = container.querySelector('#responsive-preview-status');
  const checkResponsiveBtn = container.querySelector('#check-responsive-btn');

  function applyPreviewViewport() {
    const selected = previewViewportOptions.find((item) => item.id === selectedPreviewViewportId)
      || previewViewportOptions[0];
    const isFit = selected.id === 'fit';
    const isCustom = selected.id === 'custom';
    const width = isCustom ? customPreviewWidth : selected.width;
    const height = isCustom ? customPreviewHeight : selected.height;
    const device = container.querySelector('#responsive-preview-device');
    if (device) {
      device.classList.toggle('is-fit', isFit);
      device.style.width = isFit ? '100%' : `${width}px`;
      device.style.height = isFit ? '100%' : `${height}px`;
    }
    if (previewViewportSelect) previewViewportSelect.value = selected.id;
    if (previewWidthInput) {
      previewWidthInput.value = String(width || customPreviewWidth);
      previewWidthInput.disabled = !isCustom;
    }
    if (previewHeightInput) {
      previewHeightInput.value = String(height || customPreviewHeight);
      previewHeightInput.disabled = !isCustom;
    }
    if (previewWidthSlider) {
      previewWidthSlider.value = String(Math.min(1440, width || customPreviewWidth));
      previewWidthSlider.disabled = !isCustom;
    }
    if (responsivePreviewStatus) {
      responsivePreviewStatus.textContent = isFit
        ? 'Preview: workspace size'
        : `Preview: ${selected.label} (${width} × ${height})`;
    }
  }

  previewViewportSelect?.addEventListener('change', () => {
    selectedPreviewViewportId = previewViewportSelect.value;
    applyPreviewViewport();
  });
  function applyCustomViewportInput() {
    customPreviewWidth = Math.max(240, Math.min(2560, Number(previewWidthInput?.value) || 390));
    customPreviewHeight = Math.max(320, Math.min(2560, Number(previewHeightInput?.value) || 844));
    selectedPreviewViewportId = 'custom';
    applyPreviewViewport();
  }
  previewWidthInput?.addEventListener('change', applyCustomViewportInput);
  previewHeightInput?.addEventListener('change', applyCustomViewportInput);
  previewWidthSlider?.addEventListener('input', () => {
    if (previewWidthInput) previewWidthInput.value = previewWidthSlider.value;
    applyCustomViewportInput();
  });
  applyPreviewViewport();

  let responsiveCheckRun = 0;
  async function runResponsiveCheck(html, { showDialog = false } = {}) {
    if (!html) return;
    const run = ++responsiveCheckRun;
    if (checkResponsiveBtn) checkResponsiveBtn.disabled = true;
    responsivePreviewStatus.textContent = `Checking ${RESPONSIVE_VIEWPORTS.length} screen sizes…`;
    try {
      const checks = await testResponsiveHtml(html);
      const problems = summarizeResponsiveProblems(checks);
      if (run !== responsiveCheckRun) return;
      if (!problems.length) {
        responsivePreviewStatus.textContent = `Responsive check passed: ${RESPONSIVE_VIEWPORTS.length} screen sizes.`;
      } else {
        responsivePreviewStatus.textContent = `Responsive issues found (${problems.length}).`;
        if (showDialog) await alertDialog(problems.join('\n\n'), { title: 'Responsive check' });
      }
    } catch (err) {
      if (run === responsiveCheckRun) responsivePreviewStatus.textContent = friendlyError(err, 'Could not run the responsive check.');
    } finally {
      if (run === responsiveCheckRun && checkResponsiveBtn) checkResponsiveBtn.disabled = !currentHtml;
    }
  }
  checkResponsiveBtn?.addEventListener('click', () => {
    if (!currentHtml || checkResponsiveBtn.disabled) return;
    void runResponsiveCheck(currentHtml, { showDialog: true });
  });
  if (currentHtml) void runResponsiveCheck(currentHtml);

  // If this topic was created as "Dynamic (AI-powered)" with a described
  // AI-content/processing split (see openTopicModal in views/course.js),
  // prefill the plain-language draft into the chat box so the creator can
  // review/edit it before sending — never auto-sent, so no generation (and
  // no AI cost) happens without them explicitly hitting Send. The technical
  // "how to wire up window.askAI" recipe stays in sessionStorage — it's
  // never shown here, only folded invisibly into the instruction actually
  // sent to the model the first time this topic's chat sends a message
  // (see handleSend below), the same way comment-pin locators are folded
  // in without appearing in the visible chat bubble.
  const dynamicDraftKey = `ll-dynamic-draft-${topicId}`;
  const dynamicDraft = sessionStorage.getItem(dynamicDraftKey);
  if (dynamicDraft) {
    sessionStorage.removeItem(dynamicDraftKey);
    const chatInput = container.querySelector('#chat-input');
    if (chatInput) { chatInput.value = dynamicDraft; chatInput.focus(); autoGrowChatInput(); }
  }

  // Kit-gap improvement notice (functions/kitGapFlagging.js) — set only by
  // an admin who has reviewed a real replay (functions/kitGapReplay.js) and
  // confirmed a kit fix actually helps a request like this one. Purely
  // informational and dismissible — never a regeneration, never billed,
  // the creator decides for themselves whether to ask for an update.
  if (topic.kitGapNotice) {
    kitGapNoticeBanner.style.display = 'block';
    kitGapNoticeBanner.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;">
        <span>🧠 This page was built before an improvement related to "${escapeHtml(topic.kitGapNotice.themeTitle || 'this topic')}" — you can describe the issue again in chat to see if it's fixed now.</span>
        <button type="button" class="btn btn-sm btn-ghost" id="kit-gap-notice-dismiss">Dismiss</button>
      </div>`;
    kitGapNoticeBanner.querySelector('#kit-gap-notice-dismiss').addEventListener('click', async () => {
      kitGapNoticeBanner.style.display = 'none';
      kitGapNoticeBanner.innerHTML = '';
      topic.kitGapNotice = null;
      try { await updateTopicMeta(uid, courseId, topicId, { kitGapNotice: null }); } catch (_) { /* best-effort — worst case it reappears next visit */ }
    });
  }

  container.querySelector('#back-btn').addEventListener('click', () => navigate(`/course/${courseId}`));

  // These used to double as DOM-toggling functions for template/staging
  // buttons that lived permanently above the chat input. Those rows are
  // gone now (see point 8 — moved into the setup modal's tabs below), so
  // these are just plain state setters; the modal manages its own buttons'
  // active/inactive classes locally while it's open.
  function setSelectedTemplate(id) {
    selectedTemplateId = GENERATION_TEMPLATES[id] ? id : 'auto';
  }
  function setStagingMode(mode) {
    pendingStagingMode = mode === 'cinematic' ? 'cinematic' : 'continuous';
  }
  setSelectedTemplate('auto');
  setStagingMode('continuous');

  // Auto-grows the chat prompt box to fit its content instead of the old
  // fixed rows="2" box that just scrolled no matter how much was typed.
  // Also re-run whenever the chat pane's WIDTH changes (see the splitter
  // drag below) — the same text wraps to a different number of lines at a
  // different width, so the box needs to "unfurl" to a different height
  // even though nothing was typed.
  //
  // The box is ALSO manually resizable (resize:vertical in style.css,
  // native drag handle), so this has to coexist with that rather than
  // fight it: each call checks whether the box's actual height still
  // matches what THIS function last set — if not, the user must have
  // dragged the handle since, so that height becomes a new floor
  // (chatInputManualFloorPx) instead of being discarded on the next
  // keystroke. Typing alone still stops growing at CHAT_INPUT_AUTOGROW_MAX_PX
  // (scrolling internally past that); a manual drag can go taller than
  // that on purpose — see style.css for why there's no CSS max-height.
  //
  // Queries #chat-input fresh each call (not cached) since this needs to
  // run from the splitter handler defined below, before the element is
  // queried again further down for the send-button wiring.
  const CHAT_INPUT_MIN_PX = 40;
  const CHAT_INPUT_AUTOGROW_MAX_PX = 200;
  let chatInputManualFloorPx = null;
  let chatInputLastSetPx = null;
  function autoGrowChatInput() {
    const el = container.querySelector('#chat-input');
    if (!el) return;
    if (chatInputLastSetPx !== null && Math.abs(el.offsetHeight - chatInputLastSetPx) > 1) {
      chatInputManualFloorPx = el.offsetHeight;
    }
    el.style.height = 'auto';
    const contentPx = Math.min(el.scrollHeight, CHAT_INPUT_AUTOGROW_MAX_PX);
    const target = Math.max(contentPx, chatInputManualFloorPx || 0, CHAT_INPUT_MIN_PX);
    el.style.height = `${target}px`;
    chatInputLastSetPx = target;
  }

  // ── Splitter drag ──────────────────────────────────────────────────
  // The live preview is a real <iframe> (#preview-frame) sitting directly
  // beside the splitter — a SEPARATE document/browsing context. The moment
  // a drag's cursor crosses into it, the iframe (not this window) starts
  // receiving mousemove, so the resize visibly stalls every time the mouse
  // passes over the preview; worse, releasing the mouse while over the
  // iframe means THIS window's mouseup never fires at all, leaving
  // `dragging` stuck true until the next unrelated click. That's the
  // "jerky"/sticky feel. Disabling pointer-events on the iframe for the
  // duration of the drag keeps every mouse event on this window, where the
  // listeners below actually are. Looked up fresh on each drag (not
  // cached) since the iframe element gets replaced whenever content
  // regenerates.
  const chatPane = container.querySelector('#chat-pane');
  const splitter = container.querySelector('#splitter');
  let dragging = false;

  function setPreviewInteractive(interactive) {
    const frame = container.querySelector('#preview-frame');
    if (frame) frame.style.pointerEvents = interactive ? '' : 'none';
  }

  splitter.addEventListener('mousedown', (e) => {
    e.preventDefault(); // also stops the drag from turning into a text-selection drag over the chat pane
    dragging = true;
    splitter.classList.add('dragging');
    document.body.style.userSelect = 'none';
    setPreviewInteractive(false);
  });
  window.addEventListener('mousemove', (e) => {
    if (!dragging) return;
    const bodyRect = container.querySelector('#studio-body').getBoundingClientRect();
    let pct = ((e.clientX - bodyRect.left) / bodyRect.width) * 100;
    pct = Math.min(75, Math.max(20, pct));
    chatPane.style.width = pct + '%';
    autoGrowChatInput(); // same text now wraps to a different line count at the new width
  });
  window.addEventListener('mouseup', () => {
    if (!dragging) return;
    dragging = false;
    splitter.classList.remove('dragging');
    document.body.style.userSelect = '';
    setPreviewInteractive(true);
  });

  // ── Fullscreen preview toggle ──────────────────────────────────────
  container.querySelector('#fullscreen-btn').addEventListener('click', () => {
    const frame = container.querySelector('#preview-frame');
    if (!frame?.requestFullscreen) return;
    // requestFullscreen() returns a Promise that silently rejects (no visible
    // error) if the iframe is missing the allowfullscreen/allow="fullscreen"
    // permission — this used to leave the button doing nothing with no sign
    // anything went wrong. All three places that create #preview-frame now
    // set that attribute; this catch is just a safety net + visible fallback
    // so a future regression here fails loudly instead of silently again.
    const result = frame.requestFullscreen();
    if (result?.catch) {
      result.catch((err) => {
        logClientError?.(err, 'fullscreen-preview');
        window.alert('Could not enter fullscreen — try again or check your browser settings.');
      });
    }
  });

  // ── Download generated HTML (admin only) ────────────────────────────
  // Visibility is already gated above (`if (downloadHtmlBtn && isAdminUser) ...`);
  // this handler additionally re-checks isAdminUser itself so a non-admin
  // can't trigger it even if the button were somehow made visible (e.g. via
  // devtools) — belt-and-braces, same reasoning as the Agent Activity panel
  // above. Reads `currentHtml` from the closure at click time so it always
  // reflects the latest generated/streamed/restored version, avoiding the
  // stale-`disabled`-attribute problem #fullscreen-btn has.
  if (downloadHtmlBtn) {
    downloadHtmlBtn.addEventListener('click', () => {
      if (!isAdminUser || !currentHtml) return;
      const safeName = (topic.title || 'page').trim().toLowerCase()
        .replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'page';
      const blob = new Blob([currentHtml], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeName}.html`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    });
  }

  // ── Hide-chat / preview-only toggle ─────────────────────────────────
  // The user is building their own webpage; sometimes they just want to
  // see it, full width, with nothing else on screen. This collapses the
  // chat side and splitter so the preview pane fills the whole studio —
  // toggle back any time, nothing about the chat or generation state changes.
  const toggleChatBtn = container.querySelector('#toggle-chat-btn');
  let chatHidden = false;
  toggleChatBtn.addEventListener('click', () => {
    chatHidden = !chatHidden;
    chatPane.style.display = chatHidden ? 'none' : '';
    splitter.style.display = chatHidden ? 'none' : '';
    toggleChatBtn.textContent = chatHidden ? '💬 Show chat' : '👁 Preview only';
    toggleChatBtn.title = chatHidden ? 'Bring the chat panel back' : 'Hide the chat side and show only the webpage';
  });

  // ── Version dropdown ────────────────────────────────────────────────
  function renderVersionOptions() {
    const versions = listAvailableVersions(topic.currentVersion || 0);
    if (versions.length === 0) {
      versionSelect.innerHTML = '<option value="">No saved versions</option>';
      return;
    }
    versionSelect.innerHTML = versions.map((v) =>
      `<option value="${v}" ${v === viewingVersion ? 'selected' : ''}>Version ${v}${v === topic.currentVersion ? ' (latest)' : ''}</option>`
    ).join('');
  }
  renderVersionOptions();

  // ── Spec viewer ─────────────────────────────────────────────────────
  // The component specification the version being viewed was BUILT from
  // (src/lib/topics.js's per-version beat plan). This exists because a
  // beat-built simulation that comes out wrong cannot be diagnosed from its
  // own source: the finished code cannot tell you whether a missing part was
  // never specified, was specified and skipped, or was specified wrongly.
  // The stored spec answers exactly that, and its provenance says which
  // model wrote it.
  //
  // The button only appears for versions that actually have a spec — which
  // is nearly none of them (only pages built by the beat pipeline), so it
  // must stay invisible rather than showing an empty panel.
  const viewSpecBtn = container.querySelector('#view-spec-btn');
  async function refreshSpecButton() {
    viewSpecBtn.style.display = 'none';
    if (!viewingVersion) return;
    const record = await fetchVersionBeatPlanRecord(uid, courseId, topicId, viewingVersion).catch(() => null);
    if (record) viewSpecBtn.style.display = 'inline-flex';
  }
  refreshSpecButton();

  viewSpecBtn.addEventListener('click', async () => {
    const record = await fetchVersionBeatPlanRecord(uid, courseId, topicId, viewingVersion).catch(() => null);
    if (!record) {
      flashStatus('No component specification was stored for this version.');
      return;
    }
    showSpecOverlay(container, viewingVersion, record);
  });

  versionSelect.addEventListener('change', async () => {
    const requestedVersion = Number(versionSelect.value);
    viewingVersion = requestedVersion;
    versionSelect.disabled = true;
    versionSelect.setAttribute('aria-busy', 'true');
    versionLoading.style.display = 'inline-flex';
    try {
      const html = await fetchVersionHtml(uid, courseId, topicId, requestedVersion);
      currentHtml = html;
      // Keep the spinner up until the swapped-in iframe actually finishes
      // loading, not just until updatePreview() returns — otherwise a
      // version with a heavy kit-driven animation can leave the preview
      // looking blank/frozen for a beat with no loading sign at all.
      await new Promise((resolve) => updatePreview(html, resolve));
      restoreBtn.style.display = viewingVersion === topic.currentVersion ? 'none' : 'inline-flex';
      refreshSpecButton();
    } catch (err) {
      flashStatus(friendlyError(err, `Could not load version ${requestedVersion}.`));
    } finally {
      versionSelect.disabled = false;
      versionSelect.removeAttribute('aria-busy');
      versionLoading.style.display = 'none';
    }
  });

  restoreBtn.addEventListener('click', async () => {
    if (!currentHtml) return;
    const newVersion = await saveTopicVersion(uid, courseId, topicId, currentHtml, topic.currentVersion);
    markTopicEmbeddingDirty(uid, courseId, topicId);
    topic.currentVersion = newVersion;
    viewingVersion = newVersion;
    renderVersionOptions();
    restoreBtn.style.display = 'none';
    await appendMessage(uid, courseId, topicId, { role: 'system', text: `Restored version → became v${newVersion}` });
    addSystemBubble(`Restored as version ${newVersion}`);
  });

  container.querySelector('#clear-chat-btn').addEventListener('click', async () => {
    const ok = await confirmDialog('Clear chat history for this topic? The generated page is not affected.', {
      title: 'Clear chat history?', okText: 'Clear', danger: true,
    });
    if (ok) {
      await clearChat(uid, courseId, topicId);
      chatMessagesEl.querySelectorAll('.chat-msg, .chat-user-block').forEach((el) => el.remove());
    }
  });

  // ── Load & render chat ───────────────────────────────────────────────
  const liveMessages = await loadLiveMessages(uid, courseId, topicId);
  liveMessages.forEach((m) => addBubble(m.role, m.text));
  scrollChatToBottom();

  container.querySelector('#load-older-btn').addEventListener('click', async (e) => {
    const btn = e.target;
    btn.textContent = 'Loading...';
    const archived = await loadArchivedMessages(uid, courseId, topicId);
    archiveLoaded = true;
    if (archived.length === 0) {
      btn.textContent = 'No older messages';
      btn.disabled = true;
      return;
    }
    const frag = document.createDocumentFragment();
    archived.forEach((m) => frag.appendChild(makeBubbleBlock(m.role, m.text)));
    btn.after(frag);
    btn.remove();
  });

  // ── Image attachment picker ─────────────────────────────────────────
  // Client-side check for immediate feedback (size + type); the server
  // (functions/entitlement.js) enforces the same limits independently —
  // this is UX only, not the security boundary.
  const attachImageBtn = container.querySelector('#attach-image-btn');
  const chatImageInput = container.querySelector('#chat-image-input');
  attachImageBtn.addEventListener('click', () => chatImageInput.click());

  // Shared by the file-picker (change, below) and pasting an image straight
  // into the prompt box (paste, wired further down once #chat-input is
  // queried) — same validation, same FileReader read, same result:
  // pendingImage populated and previewed exactly like a picked file.
  function attachImageFile(file) {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      flashStatus('Please choose an image file.');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      flashStatus(`Image too large — max ${Math.round(MAX_IMAGE_BYTES / (1024 * 1024))} MB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      pendingImage = {
        dataUrl: reader.result,
        name: file.name,
        sizeLabel: `${(file.size / 1024).toFixed(0)} KB`,
      };
      renderImagePreview();
    };
    reader.onerror = () => flashStatus('Could not read that image — try another file.');
    reader.readAsDataURL(file);
  }

  chatImageInput.addEventListener('change', () => {
    const file = chatImageInput.files?.[0];
    chatImageInput.value = ''; // allow re-selecting the same file later
    attachImageFile(file);
  });

  function renderImagePreview() {
    if (!pendingImage) {
      imagePreviewRow.style.display = 'none';
      imagePreviewRow.innerHTML = '';
      return;
    }
    imagePreviewRow.style.display = 'block';
    imagePreviewRow.innerHTML = `
      <div class="image-preview-chip">
        <img src="${pendingImage.dataUrl}" alt="Attached image preview" />
        <span class="image-preview-name">${escapeHtml(pendingImage.name)} · ${pendingImage.sizeLabel}</span>
        <button type="button" id="remove-image-btn" title="Remove image">×</button>
      </div>
    `;
    imagePreviewRow.querySelector('#remove-image-btn').addEventListener('click', () => {
      pendingImage = null;
      renderImagePreview();
    });
  }

  // ── Reference-content picker ─────────────────────────────────────────
  // The 🔗 button: unlike topic.referenceTopicId/referenceMode (set once,
  // in the New Topic dialog, and only ever used for a brand-new topic's
  // very first generation — see the referenceHtml load near the top of
  // renderStudio), this lets a topic that's already been generated once
  // pull in another topic's page as a reference for ONE message, any time
  // — e.g. "add a section like the one in X" mid-conversation. Picked
  // fresh per message; never persisted anywhere.
  function renderReferencePreview() {
    if (!pendingReference) {
      referencePreviewRow.style.display = 'none';
      referencePreviewRow.innerHTML = '';
      return;
    }
    referencePreviewRow.style.display = 'block';
    referencePreviewRow.innerHTML = `
      <div class="image-preview-chip">
        <span class="image-preview-name">🔗 ${escapeHtml(pendingReference.title)} — ${pendingReference.mode === 'source' ? 'same-subject source' : 'style template'}</span>
        <button type="button" id="remove-reference-btn" title="Remove reference">×</button>
      </div>
    `;
    referencePreviewRow.querySelector('#remove-reference-btn').addEventListener('click', () => {
      pendingReference = null;
      renderReferencePreview();
    });
  }

  // Resolves { courseId, topicId, title, mode } or null (cancelled). Lists
  // every topic the creator has built (any course, per the same "reference
  // existing content" scope views/course.js uses) that has a generated
  // page, excluding this topic itself.
  function openReferencePicker() {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay';
      overlay.innerHTML = `
        <div class="modal" role="dialog" aria-modal="true">
          <h2>Reference existing content</h2>
          <div class="field">
            <label for="ref-picker-select">Topic</label>
            <select id="ref-picker-select">
              <option value="">Loading…</option>
            </select>
          </div>
          <div id="ref-picker-mode-area" style="display:none;margin-top:10px;padding:8px 10px;border:1px solid var(--border);border-radius:6px;">
            <div style="font-size:0.82rem;font-weight:600;margin-bottom:6px;">What should this reference be used for?</div>
            <label style="display:flex;gap:6px;align-items:flex-start;font-size:0.82rem;margin-bottom:6px;cursor:pointer;">
              <input type="radio" name="ref-picker-mode" value="style" style="margin-top:3px;" />
              <span><strong>Style template</strong> — a different subject. Match its quality, layout and interaction style; don't reuse its content.</span>
            </label>
            <label style="display:flex;gap:6px;align-items:flex-start;font-size:0.82rem;cursor:pointer;">
              <input type="radio" name="ref-picker-mode" value="source" style="margin-top:3px;" />
              <span><strong>Same-subject source</strong> — build directly from its facts/data for this change.</span>
            </label>
          </div>
          <div class="modal-actions">
            <button type="button" class="btn btn-ghost" data-action="cancel">Cancel</button>
            <button type="button" class="btn btn-primary" data-action="attach" disabled>Attach</button>
          </div>
        </div>`;
      document.body.appendChild(overlay);
      const finish = (value) => { overlay.remove(); resolve(value); };
      overlay.addEventListener('click', (event) => { if (event.target === overlay) finish(null); });
      overlay.querySelector('[data-action="cancel"]').addEventListener('click', () => finish(null));

      const select = overlay.querySelector('#ref-picker-select');
      const modeArea = overlay.querySelector('#ref-picker-mode-area');
      const attachBtn = overlay.querySelector('[data-action="attach"]');

      function updateAttachEnabled() {
        const modeChosen = overlay.querySelector('input[name="ref-picker-mode"]:checked');
        attachBtn.disabled = !select.value || !modeChosen;
      }

      (async () => {
        try {
          const courses = await getCourses(uid);
          const perCourse = await Promise.all(courses.map(async (c) => {
            const topics = await getTopics(uid, c.id);
            return topics
              .filter((t) => t.currentVersion > 0 && !(c.id === courseId && t.id === topicId))
              .map((t) => ({ courseId: c.id, courseTitle: c.title || 'Untitled course', topicId: t.id, topicTitle: t.title || 'Untitled topic' }));
          }));
          const options = perCourse.flat();
          select.innerHTML = options.length
            ? '<option value="">— Choose a topic —</option>'
            : '<option value="">No other generated topics yet</option>';
          for (const opt of options) {
            const el = document.createElement('option');
            el.value = `${opt.courseId}::${opt.topicId}`;
            el.textContent = `${opt.courseTitle} → ${opt.topicTitle}`;
            el.dataset.courseId = opt.courseId;
            el.dataset.topicId = opt.topicId;
            el.dataset.topicTitle = opt.topicTitle;
            select.appendChild(el);
          }
        } catch (_) {
          select.innerHTML = '<option value="">Could not load topics — try again</option>';
        }
      })();

      select.addEventListener('change', () => {
        modeArea.style.display = select.value ? 'block' : 'none';
        if (!select.value) overlay.querySelectorAll('input[name="ref-picker-mode"]').forEach((r) => { r.checked = false; });
        updateAttachEnabled();
      });
      overlay.querySelectorAll('input[name="ref-picker-mode"]').forEach((r) => {
        r.addEventListener('change', updateAttachEnabled);
      });

      attachBtn.addEventListener('click', () => {
        const opt = select.selectedOptions[0];
        const mode = overlay.querySelector('input[name="ref-picker-mode"]:checked')?.value || 'style';
        if (!opt?.dataset.topicId) return;
        finish({
          courseId: opt.dataset.courseId,
          topicId: opt.dataset.topicId,
          title: opt.dataset.topicTitle,
          mode,
        });
      });
    });
  }

  const attachReferenceBtn = container.querySelector('#attach-reference-btn');
  attachReferenceBtn.addEventListener('click', async () => {
    const picked = await openReferencePicker();
    if (!picked) return;
    attachReferenceBtn.disabled = true;
    try {
      const refTopic = await getTopic(uid, picked.courseId, picked.topicId);
      if (!refTopic || !refTopic.currentVersion) {
        flashStatus('That topic has no generated content to reference.');
        return;
      }
      const html = await fetchVersionHtml(uid, picked.courseId, picked.topicId, refTopic.currentVersion);
      pendingReference = { courseId: picked.courseId, topicId: picked.topicId, title: picked.title, mode: picked.mode, html };
      renderReferencePreview();
    } catch (err) {
      flashStatus(friendlyError(err, 'Could not load that topic to reference.'));
    } finally {
      attachReferenceBtn.disabled = false;
    }
  });

  // ── Comment-on-element picker ────────────────────────────────────────
  // "💬 Comment on something" — click it, then click anything in the
  // preview. A numbered pin drops right where you clicked and a matching
  // chip shows up above the message box; you never see or type a CSS
  // selector or element name — that's all handled internally (see
  // lib/elementPicker.js) and only ever sent to the generation model, never
  // shown in the UI. Multiple pins can be dropped before sending one message.
  const pinPreviewRow = container.querySelector('#pin-preview-row');
  const pinMarkersLayer = container.querySelector('#pin-markers-layer');

  container.querySelector('#pick-element-btn').addEventListener('click', () => {
    const frame = container.querySelector('#preview-frame');
    if (!frame) return;
    pickModeOn = !pickModeOn;
    const btn = container.querySelector('#pick-element-btn');
    btn.classList.toggle('btn-primary', pickModeOn);
    btn.textContent = pickModeOn ? '💬 Click something in the page…' : '💬 Comment on something';
    if (pickModeOn) armPicker(frame); else disarmPicker(frame);
  });

  // "Check completeness" — re-runs fidelityCheck.js's narrative-fidelity
  // check on a stronger, independent model (see functions/
  // completenessOnDemand.js). The instruction it checks against is the
  // LAST message the user actually sent, taken straight from that chat
  // bubble's dataset.instruction (recoveryBanner's "Try to fix it" button
  // above uses this exact same lookup) — not retyped, since asking the user
  // to restate a brief they already gave defeats the point of a one-click
  // check.
  container.querySelector('#check-completeness-btn').addEventListener('click', async () => {
    if (isGenerating || !currentHtml || !topic.currentVersion) return;
    const lastUser = [...chatMessagesEl.querySelectorAll('.chat-user-block')].pop();
    const instruction = lastUser?.dataset.instruction;
    if (!instruction) {
      addSystemBubble("Couldn't find the request this page was built from to check against.");
      return;
    }
    const btn = container.querySelector('#check-completeness-btn');
    btn.disabled = true;
    const originalLabel = btn.textContent;
    btn.textContent = 'Checking…';
    try {
      const result = await checkPageCompleteness({
        courseId, topicId, version: topic.currentVersion, instruction,
      });
      if (!result.ran) {
        addSystemBubble("⚠️ Couldn't run the completeness check right now — try again in a moment.");
      } else if (!result.applicable) {
        addSystemBubble('✓ Checked — this looks like a single, focused request, so there\'s no multi-part checklist to verify against.');
      } else if (result.satisfied) {
        addSystemBubble(`✓ Checked against ${result.beats.length} requirement${result.beats.length === 1 ? '' : 's'} from your request — everything looks covered.`);
      } else {
        const missing = result.beats.filter((b) => !b.satisfied);
        const lines = missing.map((b) => `• ${b.description}`).join('\n');
        addSystemBubble(`⚠️ Found ${missing.length} requirement${missing.length === 1 ? '' : 's'} from your request that may be missing or incomplete:\n${lines}${result.gapSummary ? `\n\n${result.gapSummary}` : ''}\n\nDescribe a fix below, or ask to add what's missing.`);
      }
    } catch (err) {
      addSystemBubble(`Could not run the completeness check: ${friendlyError(err)}`);
    } finally {
      btn.disabled = false;
      btn.textContent = originalLabel;
    }
  });

  const rearrangeBtn = container.querySelector('#rearrange-btn');
  const cancelLayoutBtn = container.querySelector('#cancel-layout-btn');
  const finalizeLayoutBtn = container.querySelector('#finalize-layout-btn');
  const layoutEditorStatus = container.querySelector('#layout-editor-status');
  const layoutTools = container.querySelector('#layout-tools');

  function showLayoutActions(editing) {
    layoutEditing = editing;
    rearrangeBtn.style.display = editing ? 'none' : '';
    cancelLayoutBtn.style.display = editing ? '' : 'none';
    finalizeLayoutBtn.style.display = editing ? '' : 'none';
    layoutEditorStatus.style.display = editing ? 'inline' : 'none';
    layoutTools.style.display = editing ? 'flex' : 'none';
    container.querySelector('#pick-element-btn').style.display = editing ? 'none' : '';
  }

  function openLayoutEditor() {
    if (!currentHtml || isGenerating || layoutEditing) return;
    if (pickModeOn) {
      pickModeOn = false;
      disarmPicker(container.querySelector('#preview-frame'));
    }
    stopAskAIBridge();
    stopPickerBridge();
    stopLayoutEditor();
    clearPins();
    showLayoutActions(true);
    finalizeLayoutBtn.disabled = true;
    layoutEditorStatus.textContent = 'Preparing…';

    const slot = container.querySelector('#preview-frame-slot') || previewArea;
    slot.innerHTML = previewFrameMarkup();
    applyPreviewViewport();
    const frame = slot.querySelector('#preview-frame');
    detachAskAI = attachAskAIBridge(frame, {
      courseTitle: course.title, topicTitle: topic.title, courseId, topicId, ownerUid: uid,
    });
    detachQuizScore = attachQuizScoreBridge(frame, { courseId, topicId });
    detachPicker = attachElementPickerBridge(frame, { onPick: handleElementPick });
    layoutEditor = attachLayoutEditorBridge(frame, {
      onReady: () => {
        layoutEditorStatus.textContent = 'Click a card or section to work on it (▲ picks a bigger area)';
        finalizeLayoutBtn.disabled = true;
      },
      // Two distinct phases: 'picking' (choosing which card/section to edit,
      // same click-to-select feel as "Comment on something") and 'arranging'
      // (dragging that card's own parts around — nothing else on the page is
      // touchable while in this phase, so parts can never leak into a
      // different card).
      onMode: ({ mode, count, label }) => {
        const picking = mode === 'picking';
        layoutTools.style.display = picking ? 'none' : 'flex';
        if (picking) {
          layoutEditorStatus.textContent = 'Click a card or section to work on it (▲ picks a bigger area)';
          finalizeLayoutBtn.disabled = true;
        } else {
          layoutEditorStatus.textContent = count
            ? `Editing: ${label} — drag parts to reorder`
            : `Editing: ${label} — nothing else in here to rearrange`;
          finalizeLayoutBtn.disabled = false;
        }
      },
      onHistory: ({ undo, redo }) => {
        layoutTools.querySelector('[data-layout-action="undo"]').disabled = !undo;
        layoutTools.querySelector('[data-layout-action="redo"]').disabled = !redo;
      },
      onUnavailable: () => {
        layoutEditorStatus.textContent = 'Editor could not start — cancel and try again';
        finalizeLayoutBtn.disabled = true;
      },
    });
    frame.addEventListener('load', () => layoutEditor?.enable(), { once: true });
    // Assign srcdoc only after every listener is ready. The bridge also sends
    // its own loaded message and the parent retries the enable handshake, so
    // both fast and slow generated pages enter editing mode consistently.
    frame.srcdoc = injectLayoutEditorBridge(injectElementPickerBridge(injectAskAIBridge(currentHtml, { enhanceCanvas: false })));
  }

  rearrangeBtn.addEventListener('click', openLayoutEditor);
  layoutTools.querySelectorAll('[data-layout-action]').forEach((btn) => {
    btn.addEventListener('click', () => layoutEditor?.action(btn.dataset.layoutAction));
  });
  cancelLayoutBtn.addEventListener('click', () => {
    if (!layoutEditing) return;
    showLayoutActions(false);
    updatePreview(currentHtml);
  });
  finalizeLayoutBtn.addEventListener('click', async () => {
    if (!layoutEditing || !layoutEditor) return;
    finalizeLayoutBtn.disabled = true;
    cancelLayoutBtn.disabled = true;
    layoutEditorStatus.textContent = 'Saving final placement…';
    try {
      const finalizedHtml = await layoutEditor.save();
      const newVersion = await saveTopicVersion(uid, courseId, topicId, finalizedHtml, topic.currentVersion);
      markTopicEmbeddingDirty(uid, courseId, topicId);
      currentHtml = finalizedHtml;
      topic.currentVersion = newVersion;
      viewingVersion = newVersion;
      renderVersionOptions();
      versionSelect.disabled = false;
      restoreBtn.style.display = 'none';
      showLayoutActions(false);
      updatePreview(finalizedHtml);

      let updatedListings = 0;
      let syncWarning = '';
      try {
        const publishProblems = await testRenderForErrors(finalizedHtml);
        if (publishProblems.length) {
          syncWarning = ` The layout was saved, but buyers were not updated because validation found: ${publishProblems[0]}`;
        } else {
          updatedListings = await syncPublishedListingsForTopic(uid, courseId, topicId);
        }
      } catch (syncErr) {
        syncWarning = ` The layout was saved, but Library buyers could not be updated right now.`;
      }
      const buyerNote = updatedListings
        ? ` Published Library content was updated for buyers (${updatedListings} listing${updatedListings === 1 ? '' : 's'}).`
        : '';
      addSystemBubble(`Layout finalized as version ${newVersion}.${buyerNote}${syncWarning}`);
      await appendMessage(uid, courseId, topicId, {
        role: 'system',
        text: `Layout finalized → version ${newVersion}.${buyerNote}${syncWarning}`,
      });
    } catch (err) {
      layoutEditorStatus.textContent = friendlyError(err, 'Could not save the layout.');
      finalizeLayoutBtn.disabled = false;
      cancelLayoutBtn.disabled = false;
    }
  });

  // Called from the injected iframe script (see attachElementPickerBridge)
  // every time the learner clicks something while pick mode is armed.
  function handleElementPick({
    locator, label, rect, point, canvasPoint, canvasHit,
  }) {
    const frame = container.querySelector('#preview-frame');
    if (!frame) return;
    // rect/point are relative to the iframe's OWN viewport (that's all the
    // injected script can see); combining them with where the iframe
    // element itself sits in THIS page gives a position we can actually
    // draw a marker at, without ever reaching into the iframe's DOM.
    // Prefer the exact click point over the element's bounding-box center —
    // matters most for a <canvas>, where the "element" is the whole drawing
    // surface and only the click position says which part of it was meant.
    const frameRect = frame.getBoundingClientRect();
    const areaRect = previewArea.getBoundingClientRect();
    const clickLeft = point ? point.left : rect.left + rect.width / 2;
    const clickTop = point ? point.top : rect.top;
    const left = frameRect.left - areaRect.left + clickLeft;
    const top = frameRect.top - areaRect.top + clickTop;
    const selection = {
      left: frameRect.left - areaRect.left + (rect?.left ?? clickLeft),
      top: frameRect.top - areaRect.top + (rect?.top ?? clickTop),
      width: Math.max(Number(rect?.width) || 1, 1),
      height: Math.max(Number(rect?.height) || 1, 1),
    };
    if (selection.width <= 2 && selection.height <= 2) {
      const targetSize = 52;
      selection.left = left - targetSize / 2;
      selection.top = top - targetSize / 2;
      selection.width = targetSize;
      selection.height = targetSize;
    }
    pendingPins.push({
      id: `pin_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      number: pendingPins.length + 1,
      locator, label, left, top, selection, canvasPoint, canvasHit,
    });
    renderPinMarkers();
    renderPinChips();
  }

  function renderPinMarkers() {
    if (!pinMarkersLayer) return;
    pinMarkersLayer.innerHTML = pendingPins.map((p) => {
      const s = p.selection || { left: p.left - 26, top: p.top - 26, width: 52, height: 52 };
      return `
        <div class="pin-selection-box" style="left:${s.left}px;top:${s.top}px;width:${s.width}px;height:${s.height}px;" title="${escapeHtml(p.label)}"></div>
        <div class="pin-marker" style="left:${p.left}px;top:${p.top}px;" data-pin-id="${p.id}" title="${escapeHtml(p.label)} - click to remove">${p.number}</div>
      `;
    }).join('');
    pinMarkersLayer.querySelectorAll('.pin-marker').forEach((el) => {
      el.style.pointerEvents = 'auto';
      el.addEventListener('click', () => removePin(el.dataset.pinId));
    });
    return;
    pinMarkersLayer.innerHTML = pendingPins.map((p) => `
      <div class="pin-marker" style="left:${p.left}px;top:${p.top}px;" data-pin-id="${p.id}" title="${escapeHtml(p.label)} — click to remove">${p.number}</div>
    `).join('');
    pinMarkersLayer.querySelectorAll('.pin-marker').forEach((el) => {
      el.style.pointerEvents = 'auto';
      el.addEventListener('click', () => removePin(el.dataset.pinId));
    });
  }

  function renderPinChips() {
    if (!pendingPins.length) {
      pinPreviewRow.style.display = 'none';
      pinPreviewRow.innerHTML = '';
      return;
    }
    pinPreviewRow.style.display = 'block';
    pinPreviewRow.innerHTML = `<div style="display:flex;flex-wrap:wrap;gap:6px;">${pendingPins.map((p) => `
      <span class="image-preview-chip">
        <span style="font-weight:700;">📍${p.number}</span>
        <span class="image-preview-name" style="max-width:180px;">${escapeHtml(p.label)}</span>
        <button type="button" class="remove-pin-btn" data-pin-id="${p.id}" title="Remove this comment">×</button>
      </span>
    `).join('')}</div>`;
    pinPreviewRow.querySelectorAll('.remove-pin-btn').forEach((btn) => {
      btn.addEventListener('click', () => removePin(btn.dataset.pinId));
    });
  }

  function removePin(id) {
    pendingPins = pendingPins.filter((p) => p.id !== id).map((p, i) => ({ ...p, number: i + 1 }));
    renderPinMarkers();
    renderPinChips();
  }

  function clearPins() {
    pendingPins = [];
    renderPinMarkers();
    renderPinChips();
  }

  // Turns one logged canvas draw-call match (see resolveCanvasHit in
  // lib/elementPicker.js) into a compact, model-facing description built
  // from the ACTUAL numbers/colors/text that call was made with — so the
  // model can grep CURRENT FILE for them instead of reasoning about a bare
  // screen position.
  function describeCanvasMatch(m) {
    const styleBits = [
      m.style?.fillStyle ? `fill ${m.style.fillStyle}` : '',
      m.style?.strokeStyle ? `stroke ${m.style.strokeStyle}` : '',
    ].filter(Boolean).join(', ');
    const styleNote = styleBits ? ` (${styleBits})` : '';
    const u = m.userSpace || {};
    const num = (v) => (typeof v === 'number' ? v.toFixed(1) : '?');
    if (m.kind === 'circle') return `a circle${styleNote} drawn with center/radius (x=${num(u.x)}, y=${num(u.y)}, r=${num(u.r)})`;
    if (m.kind === 'rect') return `a rectangle${styleNote} drawn at (x=${num(u.x)}, y=${num(u.y)}, w=${num(u.w)}, h=${num(u.h)})`;
    if (m.kind === 'text') return `text reading "${m.text}"${m.style?.font ? ` (font ${m.style.font})` : ''} drawn at (x=${num(u.x)}, y=${num(u.y)})`;
    if (m.kind === 'image') return `an image drawn at (x=${num(u.x)}, y=${num(u.y)}, w=${num(u.w)}, h=${num(u.h)})`;
    // These come from SimKit.draw.* itself (see elementPicker.js's
    // patchSimKitDraw) rather than raw canvas primitives — real semantic
    // identity (an "arrow", a "spring") instead of an opaque generic path.
    if (m.kind === 'arrow') return `a SimKit arrow${styleNote} from (x=${num(u.x1)}, y=${num(u.y1)}) to (x=${num(u.x2)}, y=${num(u.y2)})`;
    if (m.kind === 'angleArc') return `a SimKit angle arc${styleNote} centered at (x=${num(u.cx)}, y=${num(u.cy)}), radius ${num(u.radius)}`;
    if (m.kind === 'dashedLine') return `a SimKit dashed guide line${styleNote} from (x=${num(u.x1)}, y=${num(u.y1)}) to (x=${num(u.x2)}, y=${num(u.y2)})`;
    if (m.kind === 'label') return `a SimKit label chip reading "${m.text}" at (x=${num(u.x)}, y=${num(u.y)})`;
    if (m.kind === 'spring') return `a SimKit coil spring${styleNote} from (x=${num(u.x1)}, y=${num(u.y1)}) to (x=${num(u.x2)}, y=${num(u.y2)})`;
    if (m.kind === 'rod') return `a SimKit rod${styleNote} from (x=${num(u.x1)}, y=${num(u.y1)}) to (x=${num(u.x2)}, y=${num(u.y2)})`;
    return `a drawn shape${styleNote} spanning roughly x:${num(u.x0)}-${num(u.x1)}, y:${num(u.y0)}-${num(u.y1)}`;
  }

  // One hit from a SimKit3D Scene.pickAt() call (see elementPicker.js) —
  // real object-model identity (kind/position/color/name) for a 3D scene,
  // not a guess from its 2D-projected pixels.
  function describeScene3DMatch(o) {
    const bits = [
      o.name ? `named "${o.name}"` : '',
      o.color ? `color ${o.color}` : '',
      o.position ? `at world position (x=${o.position.x?.toFixed?.(2)}, y=${o.position.y?.toFixed?.(2)}, z=${o.position.z?.toFixed?.(2)})` : '',
      (typeof o.scale === 'number') ? `scale ${o.scale}` : '',
    ].filter(Boolean).join(', ');
    return `a 3D ${o.kind || 'object'}${bits ? ` (${bits})` : ''}`;
  }

  // Renders one pin's whole canvasHit payload (matches + nearby text +
  // pixel-color fallback) into the note appended after that pin's label.
  // Empty string when the pin isn't on a canvas at all.
  function describeCanvasHit(hit) {
    if (!hit) return '';
    const parts = (hit.matches || []).slice(0, 3).map((m, i) => (i === 0 ? `directly under the click: ${describeCanvasMatch(m)}` : `also overlapping the click: ${describeCanvasMatch(m)}`));
    (hit.nearbyText || []).slice(0, 2).forEach((m) => {
      const u = m.userSpace || {};
      parts.push(`nearby label: text reading "${m.text}" at (x=${typeof u.x === 'number' ? u.x.toFixed(1) : '?'}, y=${typeof u.y === 'number' ? u.y.toFixed(1) : '?'})`);
    });
    // scene3D comes from a SimKit3D scene sharing this same <canvas> (see
    // Scene.prototype.pickAt in elementPicker.js) — sorted nearest-to-camera
    // first, same "what's actually on top" priority as the 2D matches above.
    (hit.scene3D || []).slice(0, 3).forEach((o, i) => {
      parts.push(`${i === 0 ? 'nearest 3D object at the click' : 'also near the click (3D, further back)'}: ${describeScene3DMatch(o)}`);
    });
    if (!parts.length) return hit.pixelColor ? ` No drawn shape was logged at that exact point — the pixel there samples as ${hit.pixelColor}.` : '';
    return ` — ${parts.join('; ')}.${hit.pixelColor ? ` (pixel color at click: ${hit.pixelColor})` : ''}`;
  }

  // Folds the (invisible-to-the-user) locators into the text actually sent
  // to the generation model, so it can find the exact marked element(s) in
  // CURRENT FILE instead of guessing from prose alone. What the learner
  // sees in the chat bubble stays plain — see handleSend's displayText.
  function buildInstructionWithPins(text, pins) {
    if (!pins.length) return text;
    const marked = pins
      .map((p) => {
        const hitNote = describeCanvasHit(p.canvasHit);
        const canvasNote = hitNote || (p.canvasPoint
          ? ` — clicked inside that canvas at approximately ${Math.round(p.canvasPoint.x * 100)}% across, ${Math.round(p.canvasPoint.y * 100)}% down from its top-left corner`
          : '');
        return `Pin ${p.number} — ${p.label} (locator: ${p.locator})${canvasNote}`;
      })
      .join('\n');
    const hasHitData = (p) => p.canvasHit && ((p.canvasHit.matches && p.canvasHit.matches.length) || (p.canvasHit.nearbyText && p.canvasHit.nearbyText.length) || (p.canvasHit.scene3D && p.canvasHit.scene3D.length));
    const canvasGuidance = [
      pins.some(hasHitData)
        ? ' For a pin with a canvas match, that description IS what is currently drawn at that exact point — find the matching draw call in CURRENT FILE by its coordinates/color/text (possibly reached via variables that evaluate to those numbers, not the literals themselves), not by reasoning about screen position, and change ONLY that element.'
        : '',
      pins.some((p) => p.canvasPoint && !hasHitData(p))
        ? ' For a pin that only has a canvas click position (no shape could be matched there), match that position against the canvas\'s own drawing/coordinate code in CURRENT FILE to work out what was actually drawn there.'
        : '',
    ].join('');
    // Spelled out explicitly, not left for the model to infer from the two
    // numbers sitting near each other: a bare number in the learner's
    // message (as in "remove 1") is a back-reference to the PIN with that
    // number, not a section/step/item count anywhere else on the page. Added
    // after exactly that misread happened in practice — the model removed
    // the page's first section instead of the pinned element.
    const refGuidance = pins.length === 1
      ? "When the learner's message below refers to \"1\" or \"it\"/\"this\"/\"that\", it means Pin 1 — NOT the first section, step, or item elsewhere on the page."
      : "When the learner's message below refers to a marked part by number (e.g. \"remove 1\", \"make 2 bigger\"), that number means the PIN with that number below — NOT a section, step, or item number elsewhere on the page.";
    return `The learner marked specific part(s) of the page before writing this message — locate the exact matching element(s) in CURRENT FILE using the locator(s) below (a CSS selector path).${canvasGuidance} ${refGuidance}\n${marked}\n\nLearner's message: ${text}`;
  }

  function selectedTemplate() {
    return GENERATION_TEMPLATES[selectedTemplateId] || GENERATION_TEMPLATES.auto;
  }

  function foldInTemplateBrief(text, template) {
    if (!template?.brief) return text;
    return `${template.brief}\n\nLearner's request: ${text}`;
  }

  function selectedLookFeel() {
    return LOOK_FEEL_PRESETS[selectedLookFeelId] || LOOK_FEEL_PRESETS.decide;
  }
  function selectedFont() {
    return FONT_PRESETS[selectedFontId] || FONT_PRESETS.decide;
  }
  function selectedFontSize() {
    return FONT_SIZE_PRESETS[selectedFontSizeId] || FONT_SIZE_PRESETS.decide;
  }
  function selectedBgColor() {
    return BG_COLOR_PRESETS[selectedBgColorId] || BG_COLOR_PRESETS.decide;
  }

  // Combines the whole-theme preset with any of the three finer overrides —
  // each is independently optional, so e.g. picking only "Large text" with
  // every other control left at "Let AI choose" folds in just that one line.
  function foldInLookFeelBrief(text) {
    const briefs = [selectedLookFeel().brief, selectedFont().brief, selectedFontSize().brief, selectedBgColor().brief].filter(Boolean);
    if (!briefs.length) return text; // nothing chosen — behaves exactly as before this feature existed
    return `${briefs.join('\n\n')}\n\n${text}`;
  }

  // Same invisible-fold-in idea as pins above, for a "Dynamic (AI-powered)"
  // topic's technical window.askAI wiring recipe (see buildDynamicRecipe in
  // views/course.js) — consumed (and removed) on the very first message
  // sent for this topic, so it shapes the initial generation without ever
  // appearing in the chat bubble or being resent on later edits.
  const dynamicRecipeKey = `ll-dynamic-recipe-${topicId}`;
  function foldInDynamicRecipe(text) {
    const recipe = sessionStorage.getItem(dynamicRecipeKey);
    if (!recipe) return text;
    sessionStorage.removeItem(dynamicRecipeKey);
    return `${text}\n\n${recipe}`;
  }

  function withTopicContext(text) {
    return `COURSE/TOPIC CONTEXT (must govern the generated page):
- Course: "${course.title || 'Untitled course'}"
- Topic: "${topic.title || 'Untitled topic'}"
- Topic type: "${topic.type || 'lesson'}"

Build the requested page for this exact topic. If the learner's message is broad or underspecified, infer the subject from the course and topic names; do not switch to an unrelated domain or generic demo.
Do not use physics, electrical circuits, projectile motion, biology, chemistry, or any other familiar demo domain unless the course/topic/request explicitly asks for it. For a mathematics topic, the interactive objects, controls, examples, labels, formulas, and animations must be mathematical objects from that topic.
If earlier chat history or an existing page uses an unrelated subject, ignore that subject unless the learner explicitly asks to keep it.

Learner request:
${text}`;
  }

  function needsVisualSetup(text) {
    if (currentHtml) return false;
    const kind = String(topic.type || '').toLowerCase();
    return kind === 'simulation'
      || kind === 'animation'
      || selectedTemplateId === 'sim'
      || /\b(simulation|simulator|animation|animate|animated|interactive model)\b/i.test(text);
  }

  // ── Coding-agent "bundled request" nudge ─────────────────────────────
  // A lightweight, LOCAL heuristic (no network call, no latency) for "this
  // instruction bundles several distinct asks" — sequencing words or list
  // markers. Purely advisory: never blocks sending, just offers the option
  // to scope down first, since building one thing at a time is usually much
  // faster/cheaper with the coding agent (functions/htmlAgentLoop.js's
  // write/finish loop runs more write_file rounds the more distinct things
  // one message asks for — see functions/pluginAgentHandler.js's header). A
  // false positive just means an extra confirmation click; a false negative
  // just means no nudge — neither breaks anything.
  function looksLikeMultiPartAgentRequest(text) {
    const t = String(text || '');
    const cueMatches = t.match(/\b(then|also|finally|and then|after that|next[,:]|once that'?s done)\b/gi) || [];
    const listMarkers = t.match(/(^|\n)\s*(?:\d+[.)]|[-*•])\s+/gm) || [];
    const clauseCount = t.split(/[.!?;]+/).map((s) => s.trim()).filter(Boolean).length;
    return (cueMatches.length + listMarkers.length) >= 2 || (clauseCount >= 3 && cueMatches.length >= 1);
  }

  // Resolves 'sendAnyway' or null (cancelled — the learner's typed text is
  // left untouched in the input so they can edit it down themselves).
  function confirmMultiPartAgentRequest() {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay';
      overlay.innerHTML = `
        <div class="modal" role="dialog" aria-modal="true">
          <h2>This looks like several things at once</h2>
          <p>The coding agent writes, checks its own rendering, and fixes itself in a loop — the more distinct things one message asks for, the more rounds of that loop it takes. Building one part at a time is usually much faster and cheaper.</p>
          <p>You can still send this exact message as one request — it may just take some time, since it's asking for several things at once. The result is saved automatically even if you close this tab before it finishes.</p>
          <div class="modal-actions">
            <button type="button" class="btn btn-ghost" data-action="cancel">Let me edit it</button>
            <button type="button" class="btn btn-primary" data-action="send">Send anyway</button>
          </div>
        </div>`;
      document.body.appendChild(overlay);
      const finish = (value) => { overlay.remove(); resolve(value); };
      overlay.querySelector('[data-action="cancel"]').addEventListener('click', () => finish(null));
      overlay.querySelector('[data-action="send"]').addEventListener('click', () => finish('sendAnyway'));
      overlay.addEventListener('click', (event) => { if (event.target === overlay) finish(null); });
    });
  }

  // ── Setup modal ──────────────────────────────────────────────────────
  // Point 8/9: the layout-template picker and Continuous/Sequencing
  // (staging mode) choices used to be permanent button rows above the chat
  // input, always taking up space whether or not they mattered for the
  // current message. They're now tabs inside this single information-
  // collecting modal, alongside the original sim-specific "quick setup"
  // fields (only relevant for a fresh simulation/animation topic — see
  // needsVisualSetup) and the new optional "Look & feel" gallery (point 9).
  //
  // Two ways in:
  //   - Automatically, once, before the FIRST message of a topic (see
  //     handleSend) — same timing as the old askVisualSetup, just covering
  //     more ground now.
  //   - Any time via the "⚙ Setup" button, to change the template/staging/
  //     look-&-feel choice for a topic that already has a version. In that
  //     "standalone" mode there's no student request text yet, so the modal
  //     only updates the persisted choices and resolves with no instruction
  //     text to fold in.
  function openSetupModal({ requestText = '', standalone = false } = {}) {
    const showExperienceFields = !standalone && needsVisualSetup(requestText);
    return new Promise((resolve) => {
      // Everything in here is synchronous DOM/template building — wrapped so
      // a bug in any one tab's markup (most likely the frequently-edited
      // Look & feel gallery) can never silently swallow the whole Send
      // action. Without this, an exception here propagates out of Send as
      // an unhandled rejection: the button flickers and nothing else
      // happens, with no visible error at all — see the incident this
      // guarded against, where a broken lookfeel-panel template made "Send"
      // on a brand-new topic look like it wasn't reacting.
      let overlay;
      try {
      overlay = document.createElement('div');
      overlay.className = 'modal-overlay visual-setup-overlay';
      const tabs = [
        showExperienceFields ? { id: 'setup', label: 'Quick setup' } : null,
        { id: 'template', label: 'Layout template' },
        { id: 'staging', label: 'Animation style' },
        { id: 'lookfeel', label: 'Look & feel' },
      ].filter(Boolean);
      const defaultTab = tabs[0].id;

      overlay.innerHTML = `
        <form class="modal modal-wide visual-setup-modal" id="visual-setup-form">
          <div class="visual-setup-heading">
            <div>
              <h2>${standalone ? 'Topic setup' : 'Quick setup for a better result'}</h2>
              <p>Choose what you want — every tab is optional, the suggested/default choices work well for most students.</p>
            </div>
            <span class="visual-setup-time">${standalone ? 'Saved for this topic' : 'About 1 minute'}</span>
          </div>

          <div class="setup-tabs" role="tablist">
            ${tabs.map((t) => `<button type="button" class="setup-tab ${t.id === defaultTab ? 'is-active' : ''}" data-tab="${t.id}">${t.label}</button>`).join('')}
          </div>

          ${showExperienceFields ? `
          <div class="setup-panel" data-panel="setup" style="${defaultTab === 'setup' ? '' : 'display:none;'}">
            <div class="visual-setup-grid">
              <label class="field"><span>What should we make?</span><select name="experience">
                <option value="Interactive simulation">Interactive simulation — I can change things</option>
                <option value="Guided animation">Guided animation — show it step by step</option>
                <option value="Simulation with guided animation">Both — explore and watch an explanation</option>
              </select></label>
              <label class="field"><span>How should it look?</span><select name="dimensions">
                <option value="2D">2D — clear, fast, and works on most devices</option>
                <option value="3D">3D — useful when depth really matters</option>
                <option value="Choose whichever explains the topic best">Let the AI choose</option>
              </select></label>
              <label class="field"><span>Who is it for?</span><select name="level">
                <option value="Middle or secondary school">Middle / secondary school</option>
                <option value="Primary school">Primary school</option>
                <option value="Higher secondary school">Higher secondary school</option>
                <option value="College or university">College / university</option>
              </select></label>
              <label class="field"><span>Add a short quiz?</span><select name="quiz">
                <option value="Yes, add 3 short questions with instant feedback">Yes — 3 quick questions</option>
                <option value="No quiz">No quiz</option>
                <option value="Yes, add 5 questions with instant feedback and a score">Yes — 5 questions and score</option>
              </select></label>
              <label class="field"><span>How should students control it?</span><select name="controls">
                <option value="Simple play, pause, reset, and only the most useful sliders or buttons">Simple controls</option>
                <option value="Guided next and back steps with play and pause">Step-by-step controls</option>
                <option value="More controls for open exploration, with clear safe ranges">Many exploration controls</option>
              </select></label>
              <label class="field"><span>Where will it be used most?</span><select name="device">
                <option value="Both phone and computer; use a responsive layout">Phone and computer</option>
                <option value="Mostly phone; prioritize touch and a narrow screen">Mostly phone</option>
                <option value="Mostly computer or classroom display">Mostly computer / classroom</option>
              </select></label>
            </div>
            <label class="field visual-setup-wide"><span>What should the student understand by the end? <small>(optional)</small></span>
              <input type="text" name="goal" maxlength="300" placeholder="Example: See how changing the angle changes the height">
            </label>
            <label class="field visual-setup-wide"><span>Anything that must be included? <small>(optional)</small></span>
              <input type="text" name="mustInclude" maxlength="300" placeholder="Example: labels, formula, real-life example, specific colours">
            </label>
          </div>` : ''}

          <div class="setup-panel" data-panel="template" style="${defaultTab === 'template' ? '' : 'display:none;'}">
            <p class="setup-panel-hint">Pick a layout for how this topic's content is arranged. "Auto" lets the AI decide.</p>
            <div class="template-picker-row">
              ${Object.entries(GENERATION_TEMPLATES).map(([id, t]) => templateButtonHtml(id, t)).join('')}
            </div>
          </div>

          <div class="setup-panel" data-panel="staging" style="${defaultTab === 'staging' ? '' : 'display:none;'}">
            <p class="setup-panel-hint">For 3D simulations: one continuous camera view, or distinct camera shots per scene.</p>
            <div class="staging-mode-row">
              <button type="button" class="btn btn-sm staging-mode-choice" data-staging-mode="continuous" title="One continuous camera view for the whole animation">Continuous</button>
              <button type="button" class="btn btn-sm staging-mode-choice" data-staging-mode="cinematic" title="Distinct camera shots per scene, for a request that describes multiple scenes or phases (3D simulations only)">Sequencing (staged)</button>
            </div>
          </div>

          <div class="setup-panel" data-panel="lookfeel" style="${defaultTab === 'lookfeel' ? '' : 'display:none;'}">
            <p class="setup-panel-hint">Optional — preview the general visual style before generating. This never changes layout or content, only palette/typography/decoration.</p>
            <div class="lookfeel-gallery">
              ${Object.entries(LOOK_FEEL_PRESETS).map(([id, p]) => lookFeelCardHtml(id, p)).join('')}
            </div>
            <p class="setup-panel-hint" style="margin-top:16px;">Fine-tune further — each of these is independent and optional, and combines with whichever theme you picked above.</p>
            <div class="lookfeel-fine-grid">
              <div class="lookfeel-fine-group">
                <span class="lookfeel-fine-label">Font</span>
                <div class="staging-mode-row">${fontChoiceRowHtml(FONT_PRESETS, 'font-id', 'font-choice')}</div>
              </div>
              <div class="lookfeel-fine-group">
                <span class="lookfeel-fine-label">Text size</span>
                <div class="staging-mode-row">${fontChoiceRowHtml(FONT_SIZE_PRESETS, 'fontsize-id', 'fontsize-choice')}</div>
              </div>
              <div class="lookfeel-fine-group lookfeel-fine-group-wide">
                <span class="lookfeel-fine-label">Background color</span>
                <div class="bgcolor-row">${bgColorChoiceRowHtml()}</div>
              </div>
            </div>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn btn-ghost" data-action="cancel">Cancel</button>
            <button type="submit" class="btn btn-primary">${standalone ? 'Save' : 'Use these choices'}</button>
          </div>
        </form>`;
      document.body.appendChild(overlay);
      const form = overlay.querySelector('#visual-setup-form');

      // Tab switching — plain show/hide, no framework needed for four panels.
      overlay.querySelectorAll('.setup-tab').forEach((btn) => {
        btn.addEventListener('click', () => {
          overlay.querySelectorAll('.setup-tab').forEach((b) => b.classList.toggle('is-active', b === btn));
          overlay.querySelectorAll('.setup-panel').forEach((panel) => {
            panel.style.display = panel.dataset.panel === btn.dataset.tab ? '' : 'none';
          });
        });
      });

      // Template tab — mirrors the old always-visible row's behavior, just
      // scoped to this modal instance instead of the whole chat pane.
      let localTemplateId = selectedTemplateId;
      overlay.querySelectorAll('.template-choice').forEach((btn) => {
        btn.classList.toggle('is-active', btn.dataset.templateId === localTemplateId);
        btn.addEventListener('click', () => {
          localTemplateId = btn.dataset.templateId;
          overlay.querySelectorAll('.template-choice').forEach((b) => b.classList.toggle('is-active', b === btn));
        });
      });

      // wireChoiceRow — every "pick exactly one of these buttons" row in this
      // modal does the same three things: mark the current value active when
      // the modal opens, move .is-active on click, and report the chosen
      // value back at submit time. Each row used to open-code that, which is
      // how the pipeline and completeness rows shipped adding .is-active to
      // a class NOTHING styled — the selected option looked exactly like the
      // unselected one, so there was no way to tell which was in effect.
      // Going through one helper means a new row can only get that wrong in
      // one place, and aria-pressed comes along for free so the state is
      // announced rather than only shown.
      const wireChoiceRow = (selector, dataKey, initial) => {
        const buttons = Array.from(overlay.querySelectorAll(selector));
        let value = initial;
        const paint = () => buttons.forEach((b) => {
          const on = b.dataset[dataKey] === value;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        buttons.forEach((b) => b.addEventListener('click', () => {
          value = b.dataset[dataKey];
          paint();
        }));
        paint();
        return () => value;
      };

      // Staging tab
      const getStagingMode = wireChoiceRow('.staging-mode-choice', 'stagingMode', pendingStagingMode);

      // Look & feel tab
      let localLookFeelId = selectedLookFeelId;
      overlay.querySelectorAll('.lookfeel-choice').forEach((btn) => {
        btn.classList.toggle('is-active', btn.dataset.lookfeelId === localLookFeelId);
        btn.addEventListener('click', () => {
          localLookFeelId = btn.dataset.lookfeelId;
          overlay.querySelectorAll('.lookfeel-choice').forEach((b) => b.classList.toggle('is-active', b === btn));
        });
      });
      // Fine-tune rows within the same tab — independent single-select rows,
      // same wireChoiceRow helper as staging/effort/pipeline above.
      const getFontId = wireChoiceRow('.font-choice', 'fontId', selectedFontId);
      const getFontSizeId = wireChoiceRow('.fontsize-choice', 'fontsizeId', selectedFontSizeId);
      const getBgColorId = wireChoiceRow('.bgcolor-choice', 'bgcolorId', selectedBgColorId);

      const finish = (value) => { overlay.remove(); resolve(value); };
      overlay.querySelector('[data-action="cancel"]').addEventListener('click', () => finish(null));
      overlay.addEventListener('click', (event) => { if (event.target === overlay) finish(null); });
      form.addEventListener('submit', (event) => {
        event.preventDefault();

        // Persist the three tab choices regardless of standalone vs. inline —
        // this is what lets them survive for every later send in this topic
        // session, same as before this modal existed.
        setSelectedTemplate(localTemplateId);
        setStagingMode(getStagingMode());
        selectedLookFeelId = LOOK_FEEL_PRESETS[localLookFeelId] ? localLookFeelId : 'decide';
        selectedFontId = FONT_PRESETS[getFontId()] ? getFontId() : 'decide';
        selectedFontSizeId = FONT_SIZE_PRESETS[getFontSizeId()] ? getFontSizeId() : 'decide';
        selectedBgColorId = BG_COLOR_PRESETS[getBgColorId()] ? getBgColorId() : 'decide';

        if (standalone) { finish(''); return; }

        const values = Object.fromEntries(new FormData(form).entries());
        const lines = showExperienceFields ? [
          `Format: ${values.experience}`,
          `Visual style: ${values.dimensions}`,
          `Student level: ${values.level}`,
          `Quiz: ${values.quiz}`,
          `Controls: ${values.controls}`,
          `Main device: ${values.device}`,
          values.goal && `Learning goal: ${values.goal}`,
          values.mustInclude && `Must include: ${values.mustInclude}`,
        ].filter(Boolean) : [];
        // Only wrap with the "VISUAL BUILD SETUP" framing when there's
        // actually a setup block to attach (i.e. the sim/animation-specific
        // fields rendered) — otherwise this modal's only real effect was
        // the template/staging/look-&-feel tabs (already folded in
        // separately downstream), so just pass the request through as-is.
        finish(lines.length
          ? `VISUAL BUILD SETUP (follow these choices):\n${lines.map((line) => `- ${line}`).join('\n')}\n\nStudent's original request:\n${requestText}`
          : requestText);
      });
      (form.querySelector(`[data-panel="${defaultTab}"] select, [data-panel="${defaultTab}"] .template-choice, [data-panel="${defaultTab}"] .staging-mode-choice, [data-panel="${defaultTab}"] .effort-mode-choice`))?.focus();
      } catch (err) {
        // Fail open: behave exactly as if this modal didn't exist rather
        // than blocking Send entirely. logClientError still records it so a
        // real bug here shows up in monitoring instead of only as a silent
        // "Send does nothing" report.
        console.error('[setup modal] failed to build, sending without it', err);
        logClientError({ context: 'openSetupModal', error: err, courseId, topicId });
        overlay?.remove();
        resolve(standalone ? '' : requestText);
      }
    });
  }

  container.querySelector('#open-setup-btn').addEventListener('click', () => {
    openSetupModal({ standalone: true });
  });

  // "Make dynamic" — lets a topic that was created as plain Static pick up
  // the same live-AI-content pipeline a Dynamic topic gets at creation (see
  // views/course.js's New Topic modal), without starting a new topic or
  // losing the page already built. contentMode is purely descriptive
  // metadata (see lib/topics.js's own comment on createTopic — window.askAI
  // is ALWAYS available to every generated page regardless of this flag), so
  // "converting" only needs two things: flip the flag for the UI badge, and
  // queue the same kind of technical recipe a fresh Dynamic topic gets —
  // just phrased for editing the EXISTING page in place rather than building
  // one from scratch (see buildDynamicConversionRecipe). Only rendered when
  // the topic isn't already Dynamic — see the dynamic-mode-slot markup above.
  function openMakeDynamicModal() {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay';
      overlay.innerHTML = `
        <form class="modal" id="make-dynamic-form">
          <h2>Make this topic dynamic</h2>
          <div class="info-box">
            From now on this page can ask the AI for content on demand and get a fresh answer every time a learner uses it, instead of everything being fixed. Describe what you want it to provide, and separately what the page should do with the answer — or leave both blank and just describe the change yourself in the chat box next.
          </div>
          <div class="field-row" style="margin-top:10px;">
            <label class="field"><span>What should the AI provide? <small>(optional)</small></span>
              <input type="text" id="md-ai-content" maxlength="300" placeholder="e.g. A short English paragraph (5-6 sentences) for a verb-tense exercise" autocomplete="off">
            </label>
            <label class="field"><span>What should your page do with it? <small>(optional)</small></span>
              <input type="text" id="md-ai-processing" maxlength="300" placeholder="e.g. Turn each verb into a dropdown of tense options and score the picks" autocomplete="off">
            </label>
          </div>
          <div class="modal-actions">
            <button type="button" class="btn btn-ghost" data-action="cancel">Cancel</button>
            <button type="submit" class="btn btn-primary">Make dynamic</button>
          </div>
        </form>`;
      document.body.appendChild(overlay);
      const form = overlay.querySelector('#make-dynamic-form');
      const finish = (value) => { overlay.remove(); resolve(value); };
      overlay.querySelector('[data-action="cancel"]').addEventListener('click', () => finish(null));
      overlay.addEventListener('click', (event) => { if (event.target === overlay) finish(null); });
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        finish({
          aiContent: overlay.querySelector('#md-ai-content').value.trim(),
          processing: overlay.querySelector('#md-ai-processing').value.trim(),
        });
      });
      overlay.querySelector('#md-ai-content').focus();
    });
  }

  const makeDynamicBtn = container.querySelector('#make-dynamic-btn');
  if (makeDynamicBtn) {
    makeDynamicBtn.addEventListener('click', async () => {
      const fields = await openMakeDynamicModal();
      if (fields === null) return; // cancelled — nothing changed
      makeDynamicBtn.disabled = true;
      try {
        await updateTopicMeta(uid, courseId, topicId, { contentMode: 'dynamic' });
      } catch (err) {
        makeDynamicBtn.disabled = false;
        addSystemBubble(friendlyError(err, 'Could not update this topic — try again.'));
        return;
      }
      topic.contentMode = 'dynamic';
      const slot = container.querySelector('#dynamic-mode-slot');
      if (slot) slot.innerHTML = '<span class="pill" title="This topic is meant to call the built-in AI bridge (window.askAI) at runtime rather than being fully static">🧠 Dynamic</span>';
      // Same sessionStorage key foldInDynamicRecipe already reads on every
      // Send (see its own definition above) — it doesn't care whether
      // currentHtml exists yet, so this applies to the very next message
      // regardless of whether this topic already has a generated page.
      sessionStorage.setItem(dynamicRecipeKey, buildDynamicConversionRecipe(fields));
      addSystemBubble('🧠 This topic is now Dynamic — your next message will wire up live AI content on this page instead of fixed content.');
      const draft = buildDynamicConversionDraft(fields);
      if (draft && !input.value.trim()) { input.value = draft; autoGrowChatInput(); }
      input.focus();
    });
  }

  // ── Recovery banner: a previous attempt failed and its (possibly
  // broken) output is cached in Storage. Offer to pick up where it left
  // off rather than silently losing it — this is durable across reloads,
  // not just an in-memory value. ──────────────────────────────────────
  fetchFailedAttempt(uid, courseId, topicId).then((cached) => {
    if (cached) {
      lastFailedRawHtml = cached;
      showRecoveryBanner();
    }
  });

  function showRecoveryBanner() {
    recoveryBanner.style.display = 'block';
    recoveryBanner.innerHTML = `
      A previous generation didn't finish successfully, but a partial result was saved.
      <button class="btn btn-sm" id="recover-fix-btn" style="margin-left:6px;">Try to fix it</button>
      <button class="btn btn-sm btn-ghost" id="recover-discard-btn">Discard</button>
    `;
    recoveryBanner.querySelector('#recover-fix-btn').addEventListener('click', async () => {
      const lastUser = [...chatMessagesEl.querySelectorAll('.chat-user-block')].pop();
      const instruction = lastUser?.dataset.instruction
        || 'The previous attempt did not finish successfully. Fix and complete it into a valid, working, complete HTML page.';
      hideRecoveryBanner();
      await runGeneration(instruction, { repairHtml: lastFailedRawHtml });
    });
    recoveryBanner.querySelector('#recover-discard-btn').addEventListener('click', async () => {
      lastFailedRawHtml = null;
      await clearFailedAttempt(uid, courseId, topicId);
      hideRecoveryBanner();
    });
  }
  function hideRecoveryBanner() {
    recoveryBanner.style.display = 'none';
    recoveryBanner.innerHTML = '';
  }

  // ── Live coding-agent activity panel (administrators only) ──────────────
  // See src/lib/generate.js's streamPluginAgentGeneration and
  // functions/pluginAgentLoop.js's post() events — status/assistantDelta/
  // toolCall/toolResult already stream over SSE at exactly this granularity;
  // this just stops discarding them (see runGeneration below) and renders
  // them live, admin-only, the way VS Code's own coding-agent extension shows
  // its step-by-step activity. Gated on showAgentPanel (isAdminUser) below —
  // the coding agent is the only generation pipeline now, so there is
  // nothing else for this panel to be scoped away from.
  let agentActivityPendingCalls = new Map(); // toolCall id -> entry element, until its matching toolResult arrives
  let agentActivityLiveEntry = null; // the in-progress assistantDelta bubble, until the next non-delta event finalizes it
  let agentActivityLiveBuffer = '';
  let agentActivityFlushScheduled = false;
  // toolCall id -> { chars, name, flushScheduled } for a call whose
  // arguments are still streaming in (see agentActivityHandleToolCallDelta).
  // write_file's `content` argument IS the whole HTML document, so nearly
  // all of a round's real time is spent here — before this existed, the
  // panel showed nothing at all between the turn's opening status line and
  // the eventual toolCall/toolResult pair, however long the write actually
  // took (this is what "stuck on Admin access verified" turned out to be).
  let agentActivityStreamingCalls = new Map();
  // The last full document write_file actually saved (seeded from this
  // turn's basis document — see resetAgentActivityLog) — every subsequent
  // write_file call is diffed against THIS, not against the model's own
  // previous tool call, so the diff always reads "what changed from what's
  // really on disk" even if an earlier round's write_file somehow failed.
  let agentActivityLastContent = '';
  // Tracks the most recent 'status' entry so an identical message repeating
  // back-to-back — most commonly htmlAgentLoop.js's own heartbeat "Still
  // working on it..." ping, sent every ~10s of provider silence just to keep
  // the SSE connection alive through proxies — collapses into a repeat
  // counter on the SAME line instead of a wall of visually-identical ones.
  // Scoped to kind === 'status' only (see appendAgentActivityEntry): 'pending'
  // /'live' entries are legitimately created with the same blank placeholder
  // head text before being filled in, so deduping those would wrongly merge
  // genuinely distinct tool calls.
  let agentActivityLastStatusEntry = null;
  let agentActivityLastStatusText = null;
  let agentActivityLastStatusRepeatCount = 1;

  function agentActivityNearBottom() {
    if (!agentActivityLog) return true;
    return agentActivityLog.scrollTop + agentActivityLog.clientHeight >= agentActivityLog.scrollHeight - 40;
  }
  function agentActivityScrollToBottom() {
    if (agentActivityLog) agentActivityLog.scrollTop = agentActivityLog.scrollHeight;
  }
  function resetAgentActivityLog(seedHtml) {
    if (agentActivityLog) agentActivityLog.innerHTML = '';
    agentActivityPendingCalls = new Map();
    agentActivityStreamingCalls = new Map();
    agentActivityLiveEntry = null;
    agentActivityLiveBuffer = '';
    agentActivityFlushScheduled = false;
    agentActivityLastContent = seedHtml || '';
    agentActivityLastStatusEntry = null;
    agentActivityLastStatusText = null;
    agentActivityLastStatusRepeatCount = 1;
  }
  // Every entry is a small `<div class="agent-activity-entry">` with a
  // required head line (name/status) and 3 optional sub-rows added later via
  // the setters below — body (diff stats), result (the tool's own short
  // return string), and any number of collapsible <details> blocks (the
  // actual diff text). Refs are stashed as expando properties so repeat
  // updates (there can be several per entry: pending -> streaming -> final ->
  // result) never need to re-query the DOM.
  function appendAgentActivityEntry(kind, headText) {
    if (!agentActivityLog) return null;
    // Collapse an immediate repeat of the exact same status line — see
    // agentActivityLastStatusEntry's own comment — into a counter on the
    // existing entry instead of appending a visually-identical duplicate.
    if (kind === 'status' && headText && headText === agentActivityLastStatusText && agentActivityLastStatusEntry) {
      agentActivityLastStatusRepeatCount += 1;
      setAgentActivityEntryHead(agentActivityLastStatusEntry, `${headText} (×${agentActivityLastStatusRepeatCount})`);
      return agentActivityLastStatusEntry;
    }
    const wasNearBottom = agentActivityNearBottom();
    const el = document.createElement('div');
    el.className = `agent-activity-entry is-${kind}`;
    const head = document.createElement('div');
    head.className = 'agent-activity-entry-head';
    head.textContent = headText;
    el.appendChild(head);
    el._head = head;
    agentActivityLog.appendChild(el);
    if (wasNearBottom) agentActivityScrollToBottom();
    if (kind === 'status') {
      agentActivityLastStatusEntry = el;
      agentActivityLastStatusText = headText;
      agentActivityLastStatusRepeatCount = 1;
    } else {
      // Anything else appended means real progress happened — a later
      // repeat of the same status text is a NEW occurrence, not a
      // continuation of the last one, so it should get its own fresh entry.
      agentActivityLastStatusEntry = null;
      agentActivityLastStatusText = null;
    }
    return el;
  }
  function setAgentActivityEntryHead(el, text) {
    if (el && el._head) el._head.textContent = text;
  }
  function setAgentActivityEntryBody(el, text) {
    if (!el) return;
    if (!el._body) {
      el._body = document.createElement('div');
      el._body.className = 'agent-activity-entry-body';
      el.appendChild(el._body);
    }
    el._body.textContent = text;
  }
  function setAgentActivityEntryResult(el, text, ok) {
    if (!el) return;
    if (!el._result) {
      el._result = document.createElement('div');
      el._result.className = 'agent-activity-entry-result';
      el.appendChild(el._result);
    }
    el._result.textContent = text;
    el.classList.remove('is-pending', 'is-ok', 'is-fail');
    el.classList.add(ok ? 'is-ok' : 'is-fail');
  }
  function truncateForDisplay(str, max) {
    if (str.length <= max) return str;
    return `${str.slice(0, max)}\n… truncated (${(str.length - max).toLocaleString()} more chars)`;
  }
  function addAgentActivityDetails(el, summaryText, contentText, { open = false } = {}) {
    if (!el || !contentText) return;
    const details = document.createElement('details');
    details.className = 'agent-activity-entry-details';
    if (open) details.open = true;
    const summary = document.createElement('summary');
    summary.textContent = summaryText;
    const pre = document.createElement('pre');
    pre.textContent = truncateForDisplay(contentText, 8000);
    details.appendChild(summary);
    details.appendChild(pre);
    el.appendChild(details);
  }
  // Any event other than another assistantDelta ends the current "live"
  // streaming bubble — it stops pulsing and becomes an ordinary entry.
  function finalizeAgentActivityLiveEntry() {
    if (agentActivityLiveEntry) agentActivityLiveEntry.classList.remove('is-live');
    agentActivityLiveEntry = null;
    agentActivityLiveBuffer = '';
  }
  // Batches assistantDelta text into one DOM write per animation frame — a
  // long coding-agent turn can emit hundreds to low-thousands of small text
  // deltas, and writing/scrolling on every single one causes real layout
  // churn (see plan notes). `dirty` state lives in
  // agentActivityFlushScheduled so at most one rAF is ever pending.
  function agentActivityAppendDelta(text) {
    agentActivityLiveBuffer += text;
    if (!agentActivityLiveEntry) {
      agentActivityLiveEntry = appendAgentActivityEntry('live', '');
    }
    if (agentActivityFlushScheduled) return;
    agentActivityFlushScheduled = true;
    requestAnimationFrame(() => {
      agentActivityFlushScheduled = false;
      if (!agentActivityLiveEntry) return;
      const wasNearBottom = agentActivityNearBottom();
      setAgentActivityEntryHead(agentActivityLiveEntry, agentActivityLiveBuffer);
      if (wasNearBottom) agentActivityScrollToBottom();
    });
  }
  // A cheap, dependency-free "diff": find the common prefix and common
  // suffix between the old and new document, and treat everything in
  // between as the changed region. Not a real line-level Myers diff, but
  // O(n), needs no library, and reads correctly for both this pipeline's
  // real shapes — a targeted edit (large common prefix/suffix, small middle)
  // and a full rewrite (small/no common prefix/suffix, middle ≈ whole doc).
  function computeSimpleDiff(oldStr, newStr) {
    oldStr = oldStr || ''; newStr = newStr || '';
    const maxCommon = Math.min(oldStr.length, newStr.length);
    let prefix = 0;
    while (prefix < maxCommon && oldStr[prefix] === newStr[prefix]) prefix++;
    let suffix = 0;
    const maxSuffix = maxCommon - prefix;
    while (suffix < maxSuffix && oldStr[oldStr.length - 1 - suffix] === newStr[newStr.length - 1 - suffix]) suffix++;
    return {
      removed: oldStr.slice(prefix, oldStr.length - suffix),
      added: newStr.slice(prefix, newStr.length - suffix),
      prefixLen: prefix,
      suffixLen: suffix,
    };
  }
  function agentActivityToolLabel(name, argsJson) {
    if (name === 'write_file') return 'write_file';
    if (name === 'finish') {
      try {
        const args = JSON.parse(argsJson || '{}');
        if (args.summary) return `finish — ${args.summary}`;
      } catch (_) { /* malformed args — fall through to the plain label */ }
      return 'finish';
    }
    return name;
  }
  // The rich, "very fine detail" rendering for a completed write_file call —
  // called once the FULL args (not just deltas) are in, from onToolCall
  // below. Diffs against agentActivityLastContent (what's really saved),
  // shows added/removed character counts plus how much of the document was
  // left untouched (common prefix/suffix), and an actual before/after diff
  // text in an open-by-default details block so it's visible without an
  // extra click, per the request for fine-grained visibility. Advances
  // agentActivityLastContent so the NEXT write_file diffs against this one.
  function agentActivityRenderWriteFileDiff(entry, newContent) {
    const diff = computeSimpleDiff(agentActivityLastContent, newContent);
    agentActivityLastContent = newContent;
    setAgentActivityEntryBody(
      entry,
      `${newContent.length.toLocaleString()} chars total — +${diff.added.length.toLocaleString()} / -${diff.removed.length.toLocaleString()} changed, ${(diff.prefixLen + diff.suffixLen).toLocaleString()} kept`,
    );
    const diffText = `--- removed (${diff.removed.length.toLocaleString()} chars) ---\n${diff.removed || '(nothing removed)'}\n\n--- added (${diff.added.length.toLocaleString()} chars) ---\n${diff.added || '(nothing added)'}`;
    addAgentActivityDetails(entry, 'Diff', diffText, { open: true });
  }
  // Handles toolCallDelta events — the raw provider-side streaming of a tool
  // call's arguments, arriving throughout the round (see
  // functions/htmlAgentLoop.js's forwarding comment). Creates a live entry
  // on the FIRST delta for a given id (well before the round-ending toolCall
  // event, which only fires once the whole thing has been assembled) and
  // grows a running character count on it, rAF-batched the same way
  // agentActivityAppendDelta is — these deltas can arrive at least as
  // frequently as plain text ones, often more so for a large document.
  function agentActivityToolCallDeltaLabel(name, chars) {
    if (name === 'write_file') return `write_file — writing… ${chars.toLocaleString()} chars so far`;
    if (name === 'finish') return 'finish — preparing summary…';
    return `${name || 'tool call'} — streaming… ${chars.toLocaleString()} chars`;
  }
  function agentActivityHandleToolCallDelta(evt) {
    if (!evt || !evt.id) return;
    let st = agentActivityStreamingCalls.get(evt.id);
    if (!st) {
      finalizeAgentActivityLiveEntry();
      const entry = appendAgentActivityEntry('pending', agentActivityToolCallDeltaLabel(evt.name, 0));
      st = { entry, chars: 0, name: evt.name || null, flushScheduled: false };
      agentActivityStreamingCalls.set(evt.id, st);
      agentActivityPendingCalls.set(evt.id, entry);
    }
    if (evt.name && !st.name) st.name = evt.name;
    st.chars += (evt.argumentsDelta || '').length;
    if (st.flushScheduled) return;
    st.flushScheduled = true;
    requestAnimationFrame(() => {
      st.flushScheduled = false;
      if (!st.entry) return;
      const wasNearBottom = agentActivityNearBottom();
      setAgentActivityEntryHead(st.entry, agentActivityToolCallDeltaLabel(st.name, st.chars));
      if (wasNearBottom) agentActivityScrollToBottom();
    });
  }
  function openAgentActivityPanel() {
    if (agentActivityPanel) agentActivityPanel.classList.add('is-open');
  }

  // buildAgentActivityCallbacks — the SSE handlers that render live agent
  // detail into the panel/overlay, factored out from runGeneration so it
  // stays a single, testable place rather than inline object literal noise
  // at the call site.
  function buildAgentActivityCallbacks(showAgentPanel) {
    return {
      onStatus: (_message, evt) => {
        // Backend status messages are intentionally masked for the local
        // busy text, same as the other two pipelines — but an admin
        // watching the live activity panel gets the real thing.
        if (!showAgentPanel || !evt) return;
        if (evt.type === 'assistantDelta') {
          if (evt.text) agentActivityAppendDelta(evt.text);
        } else if (evt.type === 'status') {
          finalizeAgentActivityLiveEntry();
          if (evt.message) appendAgentActivityEntry('status', evt.message);
        } else if (evt.type === 'model') {
          finalizeAgentActivityLiveEntry();
          if (evt.model) appendAgentActivityEntry('model', `Using ${evt.provider ? `${evt.provider}/` : ''}${evt.model}`);
        }
      },
      onToolCall: (evt) => {
        const sub = container.querySelector('#generating-sub');
        if (sub) {
          const label = evt.name === 'write_file' ? 'Writing the page…'
            : evt.name === 'finish' ? 'Finishing up…'
            : 'Working…';
          sub.textContent = label;
        }
        if (!showAgentPanel) return;
        finalizeAgentActivityLiveEntry();
        // This tool call's arguments may already have a live entry from its
        // own toolCallDelta stream (the common case — see
        // agentActivityHandleToolCallDelta) — reuse and finalize that ONE
        // entry rather than appending a second, duplicate line for the same
        // call. Only actually creates a new entry for a call that
        // (unusually) produced no argument deltas at all.
        agentActivityStreamingCalls.delete(evt.id);
        let entry = agentActivityPendingCalls.get(evt.id);
        if (!entry) {
          entry = appendAgentActivityEntry('pending', '');
          if (entry && evt.id) agentActivityPendingCalls.set(evt.id, entry);
        }
        if (!entry) return;
        setAgentActivityEntryHead(entry, agentActivityToolLabel(evt.name, evt.args));
        // "Very fine detail" view: the args JSON verbatim (capped —
        // truncateForDisplay handles that), plus — for write_file
        // specifically, where the args ARE the whole document — an actual
        // before/after diff against what was last saved, so it's legible
        // without eyeballing two full HTML documents.
        let args = null;
        try { args = JSON.parse(evt.args || '{}'); } catch (_) { /* malformed — no diff/raw-args detail below */ }
        if (evt.name === 'write_file' && args && typeof args.content === 'string') {
          agentActivityRenderWriteFileDiff(entry, args.content);
        } else {
          addAgentActivityDetails(entry, 'Raw args', evt.args, { open: true });
        }
      },
      onToolResult: (evt) => {
        if (!showAgentPanel || !evt || !evt.id) return;
        const entry = agentActivityPendingCalls.get(evt.id);
        agentActivityPendingCalls.delete(evt.id);
        if (!entry) return;
        const result = String(evt.result || '');
        setAgentActivityEntryResult(entry, result, !/^Error:/.test(result));
      },
      onToolCallDelta: (evt) => {
        if (!showAgentPanel) return;
        agentActivityHandleToolCallDelta(evt);
      },
      // The critic-reviewed plan (functions/beatPlanning.js's
      // planAgentBuild/critiqueAgentPlan) the agent is about to build
      // against — sent once, before the build loop starts. This pipeline has
      // no human approval step the way a plan shown for review normally
      // would, so surfacing it here is purely visibility: an admin reading
      // the activity log can sanity-check the plan itself, not just the tool
      // calls that follow it.
      onPlan: (evt) => {
        if (!showAgentPanel) return;
        const requirements = Array.isArray(evt.requirements) ? evt.requirements : [];
        const buildSteps = Array.isArray(evt.buildSteps) ? evt.buildSteps : [];
        if (!requirements.length && !buildSteps.length) return;
        const critique = evt.critique;
        const critiqueNote = critique?.ran
          ? (critique.revised ? `Critic revised the plan: ${critique.reason || 'no reason given'}` : 'Critic reviewed the plan — no changes needed.')
          : 'Critic did not run for this build.';
        const entry = appendAgentActivityEntry('status', `📋 Build plan (${requirements.length} requirement${requirements.length === 1 ? '' : 's'}, ${buildSteps.length} step${buildSteps.length === 1 ? '' : 's'})`);
        if (!entry) return;
        setAgentActivityEntryResult(entry, critiqueNote, true);
        if (requirements.length) {
          addAgentActivityDetails(entry, 'Requirements', requirements.map((r, i) => `${i + 1}. ${r}`).join('\n'), { open: true });
        }
        if (buildSteps.length) {
          addAgentActivityDetails(entry, 'Build order', buildSteps.map((s, i) => `${i + 1}. ${s.title}${s.detail ? ` — ${s.detail}` : ''}`).join('\n'), { open: true });
        }
      },
    };
  }

  if (agentActivityShowBtn) {
    agentActivityShowBtn.addEventListener('click', openAgentActivityPanel);
  }
  if (agentActivityHideBtn) {
    agentActivityHideBtn.addEventListener('click', () => {
      if (agentActivityPanel) agentActivityPanel.classList.remove('is-open');
    });
  }

  // ── Send message → generate ─────────────────────────────────────────
  const sendBtn = container.querySelector('#send-btn');
  const input = container.querySelector('#chat-input');
  sendBtn.addEventListener('click', handleSend);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  });
  input.addEventListener('input', autoGrowChatInput);
  // Ctrl+V (or right-click Paste) an image straight into the prompt box —
  // e.g. a screenshot copied from elsewhere — attaches it exactly like the
  // 📎 picker above, without needing to save it to a file first. Only
  // intercepts when the clipboard actually contains an image; pasting text
  // still behaves completely normally (default handled, not touched here).
  input.addEventListener('paste', (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageItem = Array.from(items).find((item) => item.kind === 'file' && item.type.startsWith('image/'));
    if (!imageItem) return;
    e.preventDefault();
    attachImageFile(imageItem.getAsFile());
  });

  // ── Watch a generation job that this tab did not stream ───────────────
  // Reopening this topic while a generation for it is still running
  // server-side (this tab reloaded, navigated away and back, or logged out
  // and in) shows the live "still generating" overlay from the job
  // document alone — there is no live session to reconnect to anymore (see
  // functions/agentSessionSweep.js's header), so this is purely a status
  // view, and if the run ended while the tab was away the banner below
  // picks up the finished/failed outcome and refreshes the preview.
  //
  // A job whose own updatedAt hasn't moved in longer than `generate`'s own
  // hard ceiling (3600s — see functions/index.js) plus a buffer cannot
  // still genuinely be running; the invocation holding it would already
  // have been killed by the platform. Mirrors
  // functions/topicPersistence.js's LOCK_STALE_MS exactly, so this banner
  // and the server's own reclaim-a-stale-lock logic agree on what "stuck"
  // means.
  const JOB_STALE_MS = 3600 * 1000 + 5 * 60 * 1000;
  let resumeUnsubscribe = null;
  function stopResumeWatch() {
    resumeUnsubscribe?.();
    resumeUnsubscribe = null;
  }

  // Shared by watchActiveJob's 'done' branch, and by runGeneration's catch
  // block below (this tab's own stream read failed, but the server-side
  // turn had already saved a new version by the time it's checked) — pulls
  // in whatever version the server saved without this tab's help. Returns
  // the fresh topic doc (or null) so callers can report the resolved
  // version number.
  async function reconcilePreviewIfChanged() {
    const fresh = await getTopic(uid, courseId, topicId).catch(() => null);
    if (fresh && fresh.currentVersion && fresh.currentVersion !== topic.currentVersion) {
      topic.currentVersion = fresh.currentVersion;
      topic.storagePath = fresh.storagePath;
      viewingVersion = fresh.currentVersion;
      renderVersionOptions();
      try {
        const html = await fetchVersionHtml(uid, courseId, topicId, fresh.currentVersion);
        currentHtml = html;
        updatePreview(html);
        container.querySelector('#fullscreen-btn').disabled = false;
        if (downloadHtmlBtn) downloadHtmlBtn.disabled = false;
        refreshSpecButton();
      } catch (_) { /* preview reload is best-effort — the version selector still lets them open it */ }
    }
    return fresh;
  }

  // `noticeText` takes the resolved version number and returns the system
  // bubble text — used when there's no better content to show than a canned
  // notice (the tab-reopened-after-finishing case; runGeneration's catch
  // block below has the turn's actual saved reply to show instead, so it
  // calls reconcilePreviewIfChanged directly rather than through this).
  async function reconcileFinishedJob(noticeText) {
    const fresh = await reconcilePreviewIfChanged();
    addSystemBubble(noticeText(fresh?.currentVersion ?? topic.currentVersion));
  }

  function watchActiveJob(jobId) {
    stopResumeWatch();
    isGenerating = true;
    sendBtn.disabled = true;
    resumeUnsubscribe = watchGenerationJob(uid, jobId, (job) => {
      if (job && job.status === 'running') {
        const startedMs = job.startedAt?.toMillis ? job.startedAt.toMillis() : Date.now();
        const elapsedMs = Date.now() - startedMs;
        // Staleness is measured from the last HEARTBEAT, not from the start.
        // Mirrors the server's own isLockStale exactly, so this banner and
        // the server's reclaim logic still agree on what "stuck" means.
        const lastTouchMs = job.updatedAt?.toMillis ? job.updatedAt.toMillis() : startedMs;
        if (Date.now() - lastTouchMs > JOB_STALE_MS) {
          stopResumeWatch();
          isGenerating = false;
          sendBtn.disabled = false;
          setStreaming(false);
          addSystemBubble('A generation that was started here seems to have stalled. Send another message to try again.');
          return;
        }
        const minutesAgo = Math.max(0, Math.round(elapsedMs / 60000));
        setStreaming(true, minutesAgo <= 0
          ? 'Still generating — started moments ago…'
          : `Still generating — started ${minutesAgo} minute${minutesAgo === 1 ? '' : 's'} ago…`);
        return;
      }
      // Doc missing, or status is 'done'/'failed' — either way, nothing is
      // running anymore.
      stopResumeWatch();
      isGenerating = false;
      sendBtn.disabled = false;
      setStreaming(false);
      if (job && job.status === 'done') {
        reconcileFinishedJob((v) => `Finished while you were away — now on version ${v}.`);
      } else if (job && job.status === 'failed') {
        addSystemBubble(`A generation that was running when you left this topic failed: ${job.error || 'Unknown error.'}`);
      }
    });
  }
  if (initialActiveJobId) watchActiveJob(initialActiveJobId);

  async function handleSend() {
    const typedText = input.value.trim();
    const attachedImage = pendingImage;
    const attachedReference = pendingReference;
    const pinsSnapshot = pendingPins.slice();
    if (!typedText && !attachedImage && pinsSnapshot.length === 0) return;
    if (isGenerating) return;

    // Pilot-phase gate (appConfig/access.generationRestrictedToStaff — see
    // functions/accessSettings.js): mirrors the server's own check in
    // functions/entitlement.js checkEntitlement, which is the real
    // enforcement. This is just so a non-staff user finds out before typing
    // a whole prompt rather than after a round trip.
    if (!canGenerateCached()) {
      await alertDialog(
        'Content generation is limited to school staff during this pilot — ask your school’s teacher to create this, or check back soon.',
        { title: 'Not available yet' },
      );
      return;
    }

    // BUFFERING: everything between "Send" and the generating-overlay
    // actually appearing (a possible visual-setup modal) used to have no
    // visual feedback at all — on a slow connection this reads as a dead
    // 4-5s freeze with nothing on screen acknowledging the click. Disable
    // the send button and show a pulsing status the instant Send is
    // pressed; runGeneration's own setStreaming(true) below seamlessly
    // takes over the same indicator once real generation starts, so there
    // is no gap in feedback from click to finish.
    sendBtn.disabled = true;
    setStreaming(true, 'Preparing…');
    try {
      // The setup modal now also carries the layout-template, animation-
      // style, and look-&-feel tabs (see openSetupModal) which used to be
      // permanent rows above the input regardless of topic type — so it
      // opens for every topic's FIRST message ("!currentHtml"), not only
      // the sim/animation-flavored ones the old "quick setup" fields were
      // written for. Those fields
      // still only render inside the modal when needsVisualSetup(typedText)
      // is true; otherwise the modal opens straight to the template tab.
      const setupInstruction = !currentHtml ? await openSetupModal({ requestText: typedText }) : typedText;
      if (setupInstruction === null) return;

      // Coding-agent "bundled request" nudge (see looksLikeMultiPartAgentRequest
      // above) — only relevant in that pipeline, and only once per topic-open
      // so it doesn't nag on every follow-up turn once someone's already seen
      // and dismissed it.
      let showAgentWaitNotice = false;
      if (pendingPipelineMode === 'agent' && !multiPartNudgeShown && looksLikeMultiPartAgentRequest(setupInstruction)) {
        multiPartNudgeShown = true;
        const choice = await confirmMultiPartAgentRequest();
        if (choice === null) return; // cancelled — leave the typed text as-is for editing
        showAgentWaitNotice = true;
      }

      input.value = '';
      autoGrowChatInput();
      pendingImage = null;
      renderImagePreview();
      pendingReference = null;
      renderReferencePreview();
      clearPins();
      await handleSendContinued(setupInstruction, typedText, attachedImage, attachedReference, pinsSnapshot, showAgentWaitNotice);
    } finally {
      if (!isGenerating) {
        sendBtn.disabled = false;
        setStreaming(false);
      }
    }
  }

  async function handleSendContinued(setupInstruction, typedText, attachedImage, attachedReference, pinsSnapshot, showAgentWaitNotice = false) {

    // An image/pin with no typed instruction is still a valid request —
    // give the model a sensible default rather than sending an empty string.
    const text = typedText || (pinsSnapshot.length
      ? 'Make the change described for the marked part(s) of the page.'
      : 'Use the attached image as the primary reference for what to build.');
    let modelText = setupInstruction || text;

    // What the learner SEES in the chat stays plain-language — no selector,
    // no element jargon, just a small "(N marked)" note if pins were used.
    const displayText = pinsSnapshot.length
      ? `${text} (${pinsSnapshot.length === 1 ? '1 marked element' : `${pinsSnapshot.length} marked elements`})`
      : text;
    const template = selectedTemplate();
    const displayWithTemplate = template.brief ? `[${template.marker}] ${displayText}` : displayText;
    // What actually gets SENT to the model folds in the (invisible) locators
    // — see buildInstructionWithPins — and, for a "Dynamic (AI-powered)"
    // topic's very first message, the invisible window.askAI wiring recipe
    // (see foldInDynamicRecipe above). This is also what's stored as the
    // bubble's data-instruction, so "↻ Regenerate" and the recovery banner's
    // "Try to fix it" resend the same, fully-specified instruction without
    // needing to fold the recipe in again.
    const instructionForModel = withTopicContext(foldInDynamicRecipe(foldInLookFeelBrief(foldInTemplateBrief(buildInstructionWithPins(modelText, pinsSnapshot), template))));

    // Section-targeted refinement (see src/lib/sections.js): only relevant
    // once the page is roughly big enough to risk the whole-file size cap
    // (SECTION_MODE_SUGGEST_THRESHOLD) — below that, targetSection stays
    // null and everything behaves exactly as it always has. When the page
    // IS large, try to resolve the comment-pin(s) the learner already
    // dropped (the 💬 "Comment on something" tool) to one section; if that
    // works, runGeneration below sends only that section. If it doesn't (no
    // pins, or pins landing in different/unresolvable spots), fall back to
    // sending the whole file — same as before this feature existed, same
    // size cap and all — and, once per topic, nudge toward pinning next
    // time rather than leaving a large page's slowness/size risk unexplained.
    let targetSection = null;
    if (currentHtml && currentHtml.length > SECTION_MODE_SUGGEST_THRESHOLD) {
      targetSection = resolvePinsToSection(currentHtml, pinsSnapshot);
      if (targetSection && extractSection(currentHtml, targetSection) == null) targetSection = null;
    }

    const sentBubbleEl = addBubble('user', displayWithTemplate, attachedImage?.dataUrl, instructionForModel, targetSection);
    const imageBadgeEl = sentBubbleEl?.closest('.chat-user-block')?._imageBadgeEl || null;

    if (currentHtml && currentHtml.length > SECTION_MODE_SUGGEST_THRESHOLD && !targetSection && !oversizedTipShown) {
      oversizedTipShown = true;
      addSystemBubble('💡 Tip: this page is fairly large. For a faster, more reliable update next time, tap "💬 Comment on something" and mark just the one part you want changed before sending.');
    }

    if (showAgentWaitNotice) {
      addSystemBubble('⏳ This may take some time, since it\'s asking for several things at once. Feel free to navigate away or close this tab; the coding agent keeps running server-side and your result is saved automatically.');
    }

    // The persisted chat history (Firestore) never stores image bytes, or
    // the referenced page's HTML — only note what was attached, so
    // scrollback still makes sense.
    const attachmentNote = [
      attachedImage ? '[image attached]' : null,
      attachedReference ? `[referencing "${attachedReference.title}" — ${attachedReference.mode === 'source' ? 'same-subject source' : 'style template'}]` : null,
    ].filter(Boolean).join(' ');
    const userMessageKey = createIdempotencyKey('userMessage');
    await appendMessage(uid, courseId, topicId, {
      role: 'user',
      text: attachmentNote ? `${displayWithTemplate}\n${attachmentNote}` : displayWithTemplate,
      idempotencyKey: userMessageKey,
    });
    await runGeneration(instructionForModel, {
      imageDataUrl: attachedImage?.dataUrl,
      targetSection,
      imageBadgeEl,
      attachedReference,
    });
  }

  // Core generation routine, shared by "Send" and every "↻ Regenerate"
  // button. Does NOT add a user bubble — the caller already has (or chose
  // not to duplicate) one; it only appends the assistant's response.
  //
  // repairHtml, if given (from a cached failed attempt), is sent as the
  // "current file" instead of the topic's last known-good version — the
  // model is asked to fix/finish that specific broken output rather than
  // pattern-match a fresh page from nothing, which is both cheaper and
  // more likely to converge on a second try.
  //
  // imageDataUrl, if given, is forwarded as vision input for this one
  // generation call (see lib/generate.js / functions/generateHandler.js).
  //
  // targetSection, if given and it still resolves against the basis
  // document, scopes this request to just that one section (see
  // src/lib/sections.js and SECTION_MODE_SUGGEST_THRESHOLD above) instead
  // of sending the whole file — disabled whenever repairHtml applies, since
  // that is already a "start from a specific whole document" situation.
  async function runGeneration(text, { repairHtml, imageDataUrl, targetSection, imageBadgeEl = null, attachedReference = null } = {}) {
    const generationKey = createIdempotencyKey('generationSave');
    isGenerating = true;
    sendBtn.disabled = true;
    setStreaming(true);
    const streamingBubble = addBubble('assistant', '');

    // Use only the last ~6 messages as context — the HTML artifact itself
    // is the real state (see lib/chat.js and README).
    const { loadContextMessages } = await import('../lib/chat.js');
    const recentMessages = await loadContextMessages(uid, courseId, topicId);

    const basisHtml = repairHtml || currentHtml;
    const sectionName = (!repairHtml && targetSection && basisHtml && extractSection(basisHtml, targetSection) !== null)
      ? targetSection
      : null;
    const generationInstruction = text;
    // sectionName no longer scopes the request to a fragment (that was
    // generateHandler.js's section-fragment endpoint, now deleted along with
    // the single-shot/beat pipelines) — the coding agent always sees the
    // whole document and decides for itself what to change, so there is no
    // "just this section" fast path left to announce.
    let receivedChunk = false;
    let busyIndex = 0;
    const showBusyText = () => {
      if (stopRequested) return;
      const msg = GENERATION_BUSY_TEXT[busyIndex % GENERATION_BUSY_TEXT.length];
      busyIndex += 1;
      const sub = container.querySelector('#generating-sub');
      if (sub) sub.textContent = msg;
      if (!receivedChunk) {
        streamingBubble.textContent = msg;
        scrollChatToBottom();
      }
    };
    showBusyText();
    const busyTimer = setInterval(showBusyText, 2600);

    // Declared here (not inside the try block below, its previous home) so
    // the catch block can still read it — a stream-read failure needs this
    // turn's own jobId to check whether the server-side turn actually
    // finished before offering "Regenerate", which would otherwise start a
    // second, independent turn answering the same instruction again (see
    // reconcileFinishedJob/getGenerationJob above).
    let turnSessionId = null;
    // Set when the catch block hands off to watchActiveJob instead of
    // showing a failure — suppresses this function's own finally block from
    // immediately clearing the "still generating" state watchActiveJob just
    // set.
    let handedOffToJobWatch = false;

    try {
      // The beat-plan sidecar for the CURRENT version — the component/
      // shared-state contract the previous version was built around, so this
      // turn's rewrite can be told to preserve that wiring rather than
      // quietly dissolving it while doing something unrelated
      // (functions/systemPrompt.js's contractPreservationInstructions).
      // Fails open to null; may never break a generation.
      const priorPlanRecord = topic.currentVersion
        ? await fetchVersionBeatPlanRecord(uid, courseId, topicId, topic.currentVersion).catch(() => null)
        : null;
      const priorBeatPlan = priorPlanRecord ? priorPlanRecord.beats : null;
      // turnSessionId itself is declared above the try block now (see that
      // comment) — the same id functions/generateHandler.js uses as this
      // turn's server-side job/lock id, and so the same id its chat message
      // was filed under (src/lib/chat.js upsertAssistantMessage).
      // Set from the 'final' SSE event whenever the server actually saved
      // this generation (real courseId/topicId — see canPersist in
      // generateHandler.js). The common case; null only for an edge case
      // where the server-side save itself failed (see generateHandler.js's
      // own error path for that), in which case this falls back to saving
      // from here exactly like before this feature existed.
      let serverSavedVersion = null;

      // A pinned/targeted section (sectionName) no longer routes to a
      // separate section-fragment endpoint (that was generateHandler.js's,
      // now deleted, along with the single-shot/beat pipelines it served);
      // the pin's locator context is already folded into
      // generationInstruction via buildInstructionWithPins above, and the
      // agent sees the whole document (currentHtml) and can use
      // edit_document to change just the marked part on its own judgment,
      // same as any other targeted request.
      //
      // Live agent-activity panel (administrators only). Reset now so a
      // stale previous turn's log never bleeds into this one, and reveal the
      // "show" button immediately so it's there before the first SSE event
      // can arrive.
      const showAgentPanel = isAdminUser;
      // The button's own visibility is set once at mount now (see
      // isAdminUser's declaration above) — this only resets the log content
      // and auto-opens the panel for an actual coding-agent run. A non-agent
      // run touches neither: the button stays put, the panel just keeps
      // showing whatever the last agent run in this session logged.
      if (showAgentPanel) {
        resetAgentActivityLog(basisHtml);
        openAgentActivityPanel();
      }
      const activityCallbacks = buildAgentActivityCallbacks(showAgentPanel);
      // Set from streamPluginAgentGeneration's onStoppedEarly (see
      // functions/htmlAgentLoop.js's stoppedEarly doc comment) — a provider
      // error cut the agent's turn short after real content already existed,
      // so the shipped version — OR, since 2026-08-16, a chat-only answer
      // (functions/chatAnswer.js's stoppedEarly field, the fix for a real
      // incident: a mid-sentence-truncated chat reply gave the learner no
      // signal it was cut off) — may be incomplete. Checked below in both
      // the chat-answer branch and the build branch.
      let agentStoppedEarly = false;
      // Populated from streamPluginAgentGeneration's onFinished — real
      // wall-clock duration, whether this was a refinement, and the
      // model's own (self-reported) summary of what it built/changed. Used
      // below to build the final chat bubble text — see pluginAgentHandler.js
      // for why this pipeline has no fact-based "what changed" diff of its
      // own to fall back on instead.
      let agentFinishedInfo = null;
      let agentStopReason = null;
      // Set when the chat agent (functions/chatAnswer.js's
      // answerOrEditChatTurn, the chat-first FIRST hop every message goes
      // through — see functions/pluginAgentHandler.js's header) answered or
      // made a small edit itself instead of escalating to a real build —
      // there is no page/version for this turn, just a chat reply.
      let agentAnswerText = null;
      // Captured from the SSE 'model' event (provider+model actually used
      // for this turn) regardless of whether the activity panel is open —
      // an admin must always be able to see which LLM ran, not only when
      // they happened to have the panel open at the right moment. Folded
      // into the chat bubble below instead of the (now removed, see the
      // summaryLine comment further down) self-reported summary — never
      // shown to a non-admin (see feedback_no_internal_names_in_chat: no
      // internal architecture detail belongs in a learner-visible message).
      let usedModelLabel = null;
      // The 🔗 composer button (attachedReference) wins whenever set — it's
      // a fresh, explicit choice for THIS message. Otherwise fall back to
      // the topic-level referenceHtml/referenceMode loaded at the top of
      // renderStudio, which only ever applies to a brand-new topic's very
      // first generation (same gate runGeneration's caller has always used).
      const effectiveReferenceHtml = attachedReference?.html
        || ((!topic.currentVersion && !repairHtml) ? referenceHtml : null);
      const effectiveReferenceMode = attachedReference?.mode
        || ((!topic.currentVersion && !repairHtml) ? referenceMode : null);
      turnSessionId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}-${Math.random().toString(36).slice(2, 12)}`;
      const html = await streamPluginAgentGeneration({
        instruction: generationInstruction,
        currentHtml: basisHtml,
        courseId,
        topicId,
        generationSessionId: turnSessionId,
        onSessionStarted: ({ sessionId }) => {
          activeAgentSessionId = sessionId;
          updateStopGenerationButton();
        },
        imageDataUrl,
        referenceHtml: effectiveReferenceHtml,
        referenceMode: effectiveReferenceMode,
        bgColorId: selectedBgColorId,
        templateId: selectedTemplateId,
        onImageStatus: (included) => setImageBadge(imageBadgeEl, included ? 'included' : 'failed'),
        onVersion: (v) => { serverSavedVersion = v; },
        onStoppedEarly: () => { agentStoppedEarly = true; },
        onFinished: (info) => { agentFinishedInfo = info; agentStopReason = info?.stopReason || null; },
        ...activityCallbacks,
        // Overrides activityCallbacks' own onStatus above (object literals
        // take the LAST occurrence of a key) — still calls through to it,
        // so the activity panel keeps logging exactly as before; this just
        // ALSO captures the model event independent of showAgentPanel/
        // whether the panel is currently open.
        onStatus: (message, evt) => {
          if (evt?.type === 'model' && evt.model) {
            usedModelLabel = `${evt.provider ? `${evt.provider}/` : ''}${evt.model}`;
          }
          activityCallbacks.onStatus?.(message, evt);
        },
        onAnswer: (text) => { agentAnswerText = text; },
      });
      clearInterval(busyTimer);

      // The chat agent (functions/chatAnswer.js) answered directly instead of
      // escalating to a real build (see the onAnswer callback above) — never
      // wallet-charged, but it MAY have made and saved a small edit itself
      // (checked via serverSavedVersion below), not just answered a
      // question. Render the reply as the assistant's chat message and stop,
      // before falling into the "no HTML returned" failure path below, which
      // would wrongly tell the user their build failed.
      if (agentAnswerText) {
        // A provider error cut this chat-only reply short mid-content (see
        // agentStoppedEarly's declaration above) — say so, the same way the
        // build branch below already does, instead of silently persisting a
        // truncated answer with no indication anything is missing.
        const finalAnswerText = agentStoppedEarly
          ? `${agentAnswerText}\n\n(This answer was cut short by a provider error and may be incomplete — ask again to continue it.)`
          : agentAnswerText;
        streamingBubble.textContent = finalAnswerText;
        if (turnSessionId) {
          await upsertAssistantMessage(uid, courseId, topicId, turnSessionId, finalAnswerText);
        } else {
          await appendMessage(uid, courseId, topicId, {
            role: 'assistant',
            text: finalAnswerText,
            idempotencyKey: `${generationKey}:assistantMessage`,
          });
        }
        // The chat agent made a small EDIT (not just answered a question) and
        // it actually saved — serverSavedVersion comes from the same
        // onVersion callback the build path below uses, now also fired for
        // this case (see streamPluginAgentGeneration's 'answer'-event
        // handling). Previously this branch never checked it at all: the
        // server saved a real new version, but the preview/version selector
        // just kept showing the pre-edit page for the rest of the session —
        // a real report ("I asked it to remove two elements, it said it did
        // twice, but nothing changed") that only looked fixed after a reload
        // because the save itself had genuinely already worked.
        if (serverSavedVersion) {
          currentHtml = html?.html || currentHtml;
          updatePreview(currentHtml);
          topic.currentVersion = serverSavedVersion;
          viewingVersion = serverSavedVersion;
          renderVersionOptions();
          restoreBtn.style.display = 'none';
        }
      // No setGeneratingUI/isGenerating reset needed here — this whole
      // function body runs inside one try/finally (see its bottom) that
      // already resets isGenerating/sendBtn/setStreaming on every return
      // path, including this one.
      return;
      }

      currentHtml = html;
      updatePreview(html);
      // The common case: functions/generateHandler.js already saved this
      // exact version server-side (see canPersist there) the moment its
      // pipeline finished, regardless of whether this tab was even still
      // connected by then — see functions/topicPersistence.js. Falling back
      // to a client-side save only covers the rare case where that
      // server-side save itself failed (see generateHandler.js's own
      // "saving it failed" error path, which throws instead of resolving
      // here) or an old cached build that never sent a real courseId/topicId
      // — for either, this behaves exactly as it did before this feature
      // existed.
      const newVersion = serverSavedVersion || await saveTopicVersion(uid, courseId, topicId, html, topic.currentVersion, `${generationKey}:version`);
      // Only the client-side fallback branch above needs this — when
      // serverSavedVersion is set, functions/generateHandler.js's own
      // saveGeneratedVersion call already stamped contentEmbeddingDirtyAt
      // server-side (see functions/topicPersistence.js).
      if (!serverSavedVersion) markTopicEmbeddingDirty(uid, courseId, topicId);
      topic.currentVersion = newVersion;
      viewingVersion = newVersion;
      renderVersionOptions();
      restoreBtn.style.display = 'none';
      // The spec sidecar for this new version is written just below,
      // fire-and-forget; re-check shortly after so the button appears for a
      // page that was just beat-built without needing a reload.
      setTimeout(() => { refreshSpecButton(); }, 1500);

      // The beat plan the page was built from becomes the NEXT turn's
      // contract to preserve. Carried forward on turns that DIDN'T rebuild
      // the page (the coding agent has no beat plan of its own to ship — see
      // pluginAgentHandler.js's header — so this is always the plan carried
      // over from the previous version, never a freshly generated one), so a
      // page keeps its contract across an unbroken chain of ordinary
      // refinements rather than only for the single turn that created it.
      //
      // It is ALSO the investigative record for this version — the spec the
      // page was built from, which is the only way to tell a part that was
      // never specified from one that was specified and skipped. `meta`
      // (planner model, completeness mode, critic verdict) travels with it,
      // and since the plan is always CARRIED FORWARD here, so is its
      // ORIGINAL provenance — an investigator looking at version 9 needs to
      // know its spec was written back at version 4 by a particular model,
      // not see an empty provenance just because this turn only changed a
      // colour. Fire-and-forget: it's diagnostics, and the user is already
      // looking at their page.
      const planToPersist = priorBeatPlan;
      if (planToPersist) {
        saveVersionBeatPlan(
          uid, courseId, topicId, newVersion, planToPersist,
          priorPlanRecord && priorPlanRecord.meta,
        ).catch(() => {});
      }

      // Say what changed, not just that something did. "Generated version 7"
      // gives the user no way to know the quiz stopped working; this does.
      //
      // The coding agent has no fact-based diff of its own (see
      // pluginAgentHandler.js's header) — this message is built from
      // agentFinishedInfo (see streamPluginAgentGeneration's onFinished): real
      // wall-clock duration plus the model's own self-reported summary.
      const durationLabel = agentFinishedInfo ? formatDurationLabel(agentFinishedInfo.durationMs) : null;
      const tookSuffix = durationLabel ? ` (took ${durationLabel})` : '';
      // Admin-only, always-on: which provider/model actually ran this turn.
      // Folded straight into the persisted chat text (not just the activity
      // panel) so it survives a reload and doesn't depend on the panel
      // having been open at the right moment — an admin must always be able
      // to see this, not only when they happened to be watching. Never
      // shown to a non-admin (usedModelLabel stays populated regardless of
      // showAgentPanel, but is only read here when showAgentPanel is true —
      // see feedback_no_internal_names_in_chat).
      const modelSuffix = (showAgentPanel && usedModelLabel) ? ` [${usedModelLabel}]` : '';
      let assistantText;
      if (agentStopReason === 'cancelled') {
        assistantText = `Generated version ${newVersion} with the coding agent${tookSuffix} - stopped on request, so this may be incomplete. Ask it to continue when you are ready.${modelSuffix}`;
      } else if (agentStoppedEarly) {
        assistantText = `Generated version ${newVersion} with the coding agent${tookSuffix} — stopped early after a provider error, so this may be incomplete. Ask it to continue or try again.${modelSuffix}`;
      } else {
        const verb = agentFinishedInfo?.isRefinement ? 'Amended' : 'Built';
        // The model's own self-reported summary is exactly what an admin
        // already watched stream into the Coding Agent Activity panel
        // (assistantDelta events, see buildAgentActivityCallbacks above) —
        // repeating it in the chat bubble too just shows the same text
        // twice. Only non-admins (who never see that panel — showAgentPanel
        // is isAdminUser) get the summary folded into the chat bubble; it's
        // their only way to know what changed. Admins get the model label
        // instead (see modelSuffix above).
        const summaryLine = (!showAgentPanel && agentFinishedInfo?.summary) ? ` ${verb}: ${agentFinishedInfo.summary}` : '';
        assistantText = `Generated version ${newVersion} with the coding agent${tookSuffix}.${summaryLine}${modelSuffix}`;
      }
      // This whole block OVERWRITES the message pluginAgentHandler.js already
      // wrote server-side (see the comment a few lines down) — including
      // any warning IT appended from the non-blocking render check. Without
      // this, that warning is silently erased the moment this client-side
      // rebuild lands, every time, since this reconstruction had no idea
      // the field existed. Mirrors the server's own (genericized) wording —
      // never the raw issue message text: those are internal engine/kit
      // error strings (e.g. "SimKit is not defined"), and this text is
      // persisted straight into the learner's chat history. See
      // feedback_no_internal_names_in_chat: no internal library, kit, or
      // method name may ever reach that surface.
      const issues = agentFinishedInfo?.renderIssues;
      if (issues && issues.length) {
        assistantText += agentFinishedInfo?.autoFixSucceeded
          ? ' Heads up: an automated check found something that wasn’t working correctly after generating, and one automatic fix attempt was made — please double-check that it looks right.'
          : ' Heads up: an automated check found something that may not be working correctly on this page. You may want to ask for a fix.';
      }
      // Show the SAME text live that upsertAssistantMessage persists a few
      // lines down, instead of a separate terser line — nothing re-renders
      // chat from Firestore mid-session (no onSnapshot listener here), so
      // a distinct streamingBubble string was the ONLY thing the learner
      // ever saw; the richer "what did it actually build" text silently
      // never appeared until a page reload. assistantText already carries
      // the stoppedEarly/issues wording inline, so no separate cases are
      // needed here anymore.
      streamingBubble.textContent = assistantText;
      // Overwrites the SAME message the server already wrote the moment it
      // saved this version (functions/topicPersistence.js
      // upsertAssistantMessage, keyed by this exact turnSessionId) with the
      // fuller "what changed / what may have regressed" text — rather than
      // appending a second, duplicate message for this one turn. Falls back
      // to a plain append only if turnSessionId is somehow unset (an old
      // cached build), matching the pre-existing behavior for that case.
      if (turnSessionId) {
        await upsertAssistantMessage(uid, courseId, topicId, turnSessionId, assistantText);
      } else {
        await appendMessage(uid, courseId, topicId, {
          role: 'assistant',
          text: assistantText,
          idempotencyKey: `${generationKey}:assistantMessage`,
        });
      }

      container.querySelector('#fullscreen-btn').disabled = false;
      if (downloadHtmlBtn) downloadHtmlBtn.disabled = false;
      versionSelect.disabled = false;

      // A generation just succeeded — any previously cached failed
      // attempt is no longer relevant.
      if (lastFailedRawHtml) {
        lastFailedRawHtml = null;
        clearFailedAttempt(uid, courseId, topicId).catch(() => {});
        hideRecoveryBanner();
      }
    } catch (err) {
      clearInterval(busyTimer);

      // A stream-read/network error here does NOT mean the server-side turn
      // didn't complete — every path that answers a turn (a real build, a
      // chat-driven edit, or a plain chat answer — see
      // functions/pluginAgentHandler.js) writes its own assistant message
      // and, for a build/edit, saves the new version, independent of
      // whether this browser is still connected (functions/
      // topicPersistence.js's header). Check for that BEFORE treating this
      // as a failure:
      //  - the turn's own assistant message already exists → it finished;
      //    show the real reply (and pull in the new version, if any)
      //    instead of a "Generation failed" bubble.
      //  - no message yet, but the job doc says still 'running' → a build
      //    is still actively working server-side; switch to watching it
      //    instead of starting a second, independent turn that would answer
      //    the same instruction a second time right underneath the first —
      //    the actual cause of the repeated-chat-entries reports this fixes.
      // Only a turn that's genuinely missing/failed falls through to the
      // ordinary failure handling below, where "Regenerate" starting a
      // fresh turn is correct.
      if (turnSessionId) {
        // Retried a few times with a short delay rather than checked once: a
        // disconnect that happens very early in a turn can race the
        // server's own job-doc write (functions/pluginAgentHandler.js's
        // setGenJob) — checking only once could land in that gap and show a
        // false "failed" for a turn that's actually fine and about to show
        // up. A genuine terminal failure (job truly missing, or never
        // reaches 'running') still falls through to the failure UI below
        // once these run out.
        for (let attempt = 0; attempt < 3; attempt += 1) {
          const existingMsg = await getAssistantMessage(uid, courseId, topicId, turnSessionId).catch(() => null);
          if (existingMsg) {
            await reconcilePreviewIfChanged();
            streamingBubble.textContent = existingMsg.text;
            versionSelect.disabled = false;
            // This turn genuinely succeeded (just over a connection that
            // dropped before the client found out) — same cleanup the normal
            // success path does below.
            if (lastFailedRawHtml) {
              lastFailedRawHtml = null;
              clearFailedAttempt(uid, courseId, topicId).catch(() => {});
              hideRecoveryBanner();
            }
            return;
          }
          const job = await getGenerationJob(uid, turnSessionId).catch(() => null);
          if (job && job.status === 'running') {
            streamingBubble.remove();
            handedOffToJobWatch = true;
            watchActiveJob(turnSessionId);
            return;
          }
          if (attempt < 2) await new Promise((resolve) => setTimeout(resolve, 1500));
        }
      }

      const displayMessage = `⚠️ ${friendlyError(err, 'Generation failed. Please try again.')}`;
      streamingBubble.textContent = displayMessage;
      streamingBubble.classList.add('system');

      // Centralized, mass-explorable log (see lib/errorLog.js) — separate
      // from the friendly text above, this keeps the exact technical
      // reason (err.name/stack, and whichever of getIdToken/fetch/
      // stream-read/http/server/empty-response stage it failed at — see
      // lib/generate.js) so failures across every user can be analyzed in
      // aggregate, not just shown once to whoever hit it.
      await logClientError({
        context: 'generate',
        error: err,
        displayMessage,
        courseId, topicId,
        extra: { topicType: topic.type || null },
      });

      // Cache whatever the model produced (if anything) so a "Regenerate"
      // click can ask it to fix/finish this attempt instead of starting
      // over — and so this survives a reload even if the user does
      // nothing right now. See functions/generateHandler.js / lib/generate.js
      // for how rawHtml gets attached to the thrown error. In section mode,
      // rawHtml (if present) is just the one section's fragment, not a
      // whole page — never cache that as a whole-file "repair" basis, since
      // resending it as `currentHtml` would corrupt the page.
      if (err.rawHtml && !sectionName) {
        lastFailedRawHtml = err.rawHtml;
        saveFailedAttempt(uid, courseId, topicId, err.rawHtml).catch(() => {});
      }

      // Always offer a way to try again, right under the failed response —
      // regardless of whether this was a validation failure, a network
      // drop, or anything else that kept a response from coming through.
      const retryBtn = document.createElement('button');
      retryBtn.className = 'btn btn-sm';
      retryBtn.style.marginTop = '8px';
      retryBtn.textContent = '↻ Regenerate';
      retryBtn.addEventListener('click', async () => {
        retryBtn.disabled = true;
        if (imageBadgeEl) setImageBadge(imageBadgeEl, 'pending');
        await runGeneration(text, {
          repairHtml: sectionName ? null : (err.rawHtml || lastFailedRawHtml),
          imageDataUrl,
          targetSection: sectionName || targetSection || null,
          imageBadgeEl,
        });
      });
      streamingBubble.after(retryBtn);
      scrollChatToBottom();
    } finally {
      // Skipped when the catch block above handed off to watchActiveJob
      // (job still 'running' server-side) — that call already set
      // isGenerating/sendBtn/setStreaming for the still-in-progress state,
      // and this would otherwise immediately clear it back out.
      if (!handedOffToJobWatch) {
        isGenerating = false;
        sendBtn.disabled = false;
        setStreaming(false);
      }
      // If the request failed (network drop, server error) before an
      // imageStatus event ever arrived, the badge would otherwise be stuck
      // showing "…" forever, implying it's still being sent. Only touches
      // it while still pending — an image already confirmed 'included'
      // stays that way even if something ELSE about this turn failed later.
      if (imageBadgeEl && imageBadgeEl.classList.contains('chat-msg-image-badge-pending')) {
        setImageBadge(imageBadgeEl, 'failed');
      }
    }
  }

  // Two visible signals while a generation is in flight, not just the small
  // topbar pill (reported as too easy to miss): a badge up top AND a large
  // overlay right over the preview pane — the thing the user is actually
  // looking at — with a spinner, status line, and shimmering progress bar.
  function setStreaming(on, label) {
    streamStatus.innerHTML = on
      ? `<span class="streaming-indicator"><span class="dot-pulse"></span> ${escapeHtml(label || 'Generating…')}</span>`
      : '';
    // label is only used for the lightweight topbar indicator during the
    // pre-generation "Preparing…" buffering phase (see handleSend) — the
    // full generating-overlay (with its own busy-text rotation) is reserved
    // for actual generation via runGeneration, so it only opens for the
    // default "Generating…" call, not the preparatory one.
    if (label) return;
    const overlay = container.querySelector('#generating-overlay');
    if (overlay) {
      overlay.style.display = on ? 'flex' : 'none';
      if (on) {
        activeAgentSessionId = null;
        stopRequested = false;
        const sub = overlay.querySelector('#generating-sub');
        if (sub) sub.textContent = 'Starting…';
        // Cleared at the start of every generation — only shown again once
        // (if) the server actually sends a real average (see onEstimate in
        // runGeneration below). Never carries a stale estimate over from a
        // previous generation into one that hasn't reported yet.
        const estimate = overlay.querySelector('#generating-estimate');
        if (estimate) { estimate.style.display = 'none'; estimate.textContent = ''; }
        updateStopGenerationButton();
      } else {
        activeAgentSessionId = null;
        stopRequested = false;
        updateStopGenerationButton();
      }
    }
  }

  function flashStatus(msg) {
    streamStatus.textContent = msg;
    setTimeout(() => { if (streamStatus.textContent === msg) streamStatus.textContent = ''; }, 1800);
  }

  function updateStopGenerationButton() {
    const stopBtn = container.querySelector('#stop-generation-btn');
    if (!stopBtn) return;
    const canStop = isGenerating && activeAgentSessionId && !stopRequested;
    stopBtn.disabled = !canStop;
    stopBtn.textContent = stopRequested ? 'Stopping...' : 'Stop';
    stopBtn.title = stopRequested
      ? 'Stopping after the current step finishes'
      : activeAgentSessionId
        ? 'Stop generation after the current step finishes'
        : 'Stop will be available once the agent starts';
  }

  async function requestGenerationStop() {
    if (!isGenerating || !activeAgentSessionId || stopRequested) return;
    stopRequested = true;
    updateStopGenerationButton();
    setStreaming(true, 'Stopping...');
    const sub = container.querySelector('#generating-sub');
    if (sub) sub.textContent = 'Stopping after the current step finishes...';
    try {
      const result = await stopAgentSession(activeAgentSessionId);
      if (result?.message) {
        const stopSub = container.querySelector('#generating-sub');
        if (stopSub) stopSub.textContent = result.message;
      }
    } catch (err) {
      stopRequested = false;
      updateStopGenerationButton();
      flashStatus(friendlyError(err, 'Could not stop this run. Try again.'));
    }
  }

  container.querySelector('#stop-generation-btn')?.addEventListener('click', requestGenerationStop);

  // ── Coding-agent session controls ────────────────────────────────────────
  // Stop is cooperative: the backend finishes the current step, then saves
  // whatever the agent has built so far.
  //
  // Deliberately REMOVED: there are no Stop/interject controls anymore. A
  // run lives exactly as long as this tab's connection (see
  // functions/agentSessionSweep.js's header) — the server aborts it when
  // the browser disconnects, so closing the tab IS the stop. The #agent-stop
  // / #agent-interject UI was removed with the live-session concept.

  function updatePreview(html, onFrameLoad) {
    stopAskAIBridge();
    stopPickerBridge();
    stopLayoutEditor();
    layoutEditing = false;
    // Any pins the learner dropped refer to exact positions in the OLD
    // rendered HTML — meaningless (and potentially pointing at the wrong
    // spot) against a freshly regenerated page, so they don't carry over.
    clearPins();
    // Only replace the frame slot, not the whole #preview-area — the
    // generating overlay lives as a sibling in #preview-area and must
    // survive this (it's still visible until setStreaming(false) runs
    // right after this in the finally block).
    const slot = container.querySelector('#preview-frame-slot') || previewArea;
    slot.innerHTML = previewFrameMarkup();
    applyPreviewViewport();
    const frame = slot.querySelector('#preview-frame');
    // srcdoc parses/executes asynchronously — a page with a kit-driven
    // animation (canvas setup, physics init, etc.) can stay visibly blank
    // for a beat after this call returns, so callers that show their own
    // loading sign (e.g. the version-select dropdown below) pass a callback
    // to keep it up until the frame has actually finished loading.
    if (onFrameLoad) frame.addEventListener('load', onFrameLoad, { once: true });
    frame.srcdoc = injectElementPickerBridge(injectAskAIBridge(html, { enhanceCanvas: false }));
    detachAskAI = attachAskAIBridge(frame, {
      courseTitle: course.title, topicTitle: topic.title, courseId, topicId, ownerUid: uid,
    });
    detachQuizScore = attachQuizScoreBridge(frame, { courseId, topicId });
    detachPicker = attachElementPickerBridge(frame, { onPick: handleElementPick });
    const pickBtn = container.querySelector('#pick-element-btn');
    if (pickBtn) pickBtn.disabled = false;
    const arrangeBtn = container.querySelector('#rearrange-btn');
    if (arrangeBtn) arrangeBtn.disabled = false;
    const completenessBtn = container.querySelector('#check-completeness-btn');
    if (completenessBtn) completenessBtn.disabled = false;
    if (previewViewportSelect) previewViewportSelect.disabled = false;
    if (checkResponsiveBtn) checkResponsiveBtn.disabled = false;
    // Every saved/generated version receives the same device-matrix audit
    // automatically; the toolbar button repeats it on demand with details.
    void runResponsiveCheck(html);
    // Pick mode itself (as opposed to the pins) is a standing preference —
    // if the learner had it toggled on, keep it on for the new page too.
    if (pickModeOn) armPicker(frame);
  }

  function addBubble(role, text, imageDataUrl, instructionOverride, targetSection) {
    if (role === 'user') {
      const block = makeBubbleBlock(role, text, imageDataUrl, instructionOverride, targetSection);
      chatMessagesEl.appendChild(block);
      scrollChatToBottom();
      return block.querySelector('.chat-msg');
    }
    const el = document.createElement('div');
    el.className = `chat-msg ${role}`;
    el.textContent = text;
    chatMessagesEl.appendChild(el);
    scrollChatToBottom();
    return el;
  }
  function addSystemBubble(text) { addBubble('system', text); }

  // User messages are wrapped in a small block with a "↻ Regenerate"
  // button underneath, per request — lets the user re-ask the same
  // question if a response never came through cleanly, without retyping it.
  // If an image was attached, its thumbnail is shown inside the bubble and
  // the full data URL is kept on the element (in memory only, never
  // persisted) so Regenerate can resend the same image within this session.
  //
  // instructionOverride is what actually gets (re)sent to the model — for a
  // message built from comment pins (see handleSend), that's `text` plus
  // the folded-in (invisible) element locators, while the bubble itself
  // only ever displays the plain-language `text`. targetSection (if any —
  // see handleSend/SECTION_MODE_SUGGEST_THRESHOLD) is stashed so this same
  // message's own "↻ Regenerate" button repeats a section-targeted request
  // as a section-targeted request, rather than silently falling back to
  // the whole file.
  function makeBubbleBlock(role, text, imageDataUrl, instructionOverride, targetSection) {
    if (role !== 'user') {
      const el = document.createElement('div');
      el.className = `chat-msg ${role}`;
      el.textContent = text;
      return el;
    }
    const block = document.createElement('div');
    block.className = 'chat-user-block';
    block.dataset.instruction = instructionOverride || withTopicContext(text);
    block.dataset.targetSection = targetSection || '';
    block._instructionImage = imageDataUrl || null;
    block.style.cssText = 'display:flex;flex-direction:column;align-items:flex-end;gap:4px;';

    const bubble = document.createElement('div');
    bubble.className = 'chat-msg user';
    if (imageDataUrl) {
      const imgWrap = document.createElement('div');
      imgWrap.className = 'chat-msg-image-wrap';
      const img = document.createElement('img');
      img.className = 'chat-msg-image';
      img.src = imageDataUrl;
      img.alt = 'Attached image';
      const badge = document.createElement('span');
      badge.className = 'chat-msg-image-badge';
      imgWrap.appendChild(img);
      imgWrap.appendChild(badge);
      bubble.appendChild(imgWrap);
      // Stashed so runGeneration (below) can update it once the server
      // reports back whether this image actually reached the model — see
      // setImageBadge. Starts 'pending' the instant the bubble appears.
      block._imageBadgeEl = badge;
      setImageBadge(badge, 'pending');
    }
    const textNode = document.createElement('span');
    textNode.textContent = text;
    bubble.appendChild(textNode);
    block.appendChild(bubble);

    const retryBtn = document.createElement('button');
    retryBtn.className = 'btn btn-sm btn-ghost';
    retryBtn.style.cssText = 'font-size:0.72rem;padding:2px 8px;';
    retryBtn.textContent = '↻ Regenerate';
    retryBtn.title = 'Ask again for this message, in case a response could not be received';
    retryBtn.addEventListener('click', async () => {
      if (isGenerating) return;
      if (block._imageBadgeEl) setImageBadge(block._imageBadgeEl, 'pending');
      await runGeneration(block.dataset.instruction, {
        repairHtml: null, imageDataUrl: block._instructionImage || null, targetSection: block.dataset.targetSection || null, imageBadgeEl: block._imageBadgeEl || null,
      });
    });
    block.appendChild(retryBtn);

    return block;
  }

  function scrollChatToBottom() { chatMessagesEl.scrollTop = chatMessagesEl.scrollHeight; }

  return () => { stopAskAIBridge(); stopPickerBridge(); stopLayoutEditor(); stopResumeWatch(); };
}

function summarizeStreamingText(assembled) {
  const len = assembled.length;
  return `Generating… (${len.toLocaleString()} characters so far)`;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// Small status badge on an attached chat image (see makeBubbleBlock) —
// 'pending' the instant the message is sent, then 'included' once the
// server confirms the image actually reached the model (attached directly
// for a vision-capable provider, or successfully described for one that
// isn't — see functions/generationShared.js's describeImageForTextOnlyModel,
// reused by functions/pluginAgentHandler.js), or 'failed' if a non-vision provider's
// description call itself failed, meaning the turn proceeded WITHOUT any
// image context. Directly answers "was my image actually used", which
// used to be silently unknowable (an image attached during the coding-
// agent pipeline was dropped with no indication at all).
function setImageBadge(badgeEl, status) {
  if (!badgeEl) return;
  badgeEl.classList.remove('chat-msg-image-badge-pending', 'chat-msg-image-badge-ok', 'chat-msg-image-badge-failed');
  if (status === 'included') {
    badgeEl.classList.add('chat-msg-image-badge-ok');
    badgeEl.textContent = '✓';
    badgeEl.title = 'Image was sent to the AI and used for this response.';
  } else if (status === 'failed') {
    badgeEl.classList.add('chat-msg-image-badge-failed');
    badgeEl.textContent = '!';
    badgeEl.title = "Couldn't process this image — the response was generated without it.";
  } else {
    badgeEl.classList.add('chat-msg-image-badge-pending');
    badgeEl.textContent = '…';
    badgeEl.title = 'Sending to the AI…';
  }
}

// ── Spec overlay ───────────────────────────────────────────────────────────
// Renders the stored component specification for one version: what each part
// was told to be, how it was told to relate to the others, and who wrote the
// instruction. This is the investigative view for "the simulation is wrong"
// — the whole point is to distinguish a part that was never specified from
// one that was specified and then built badly, which the generated source
// alone can never tell you.
//
// Deliberately read-only and self-contained (inline styles, no new CSS
// file): a diagnostic panel that has to be kept in sync with a stylesheet is
// a diagnostic panel that rots.
function specFieldRow(label, value) {
  if (!value) return '';
  return `<div style="margin-top:6px;"><span style="opacity:0.65;font-size:0.78rem;text-transform:uppercase;letter-spacing:0.04em;">${escapeHtml(label)}</span><div style="white-space:pre-wrap;">${escapeHtml(value)}</div></div>`;
}

function specBeatCard(beat, allBeats) {
  const d = beat.detail || null;
  const byId = new Map((allBeats || []).map((b) => [b.id, b]));
  const depLabels = (beat.dependsOn || [])
    .map((id) => (byId.get(id) || {}).label || id)
    .join(', ');
  const corr = beat.correlation
    ? (beat.correlation.type === 'formula'
      ? `follows ≈ ${beat.correlation.formula} of ${(byId.get(beat.correlation.withId) || {}).label || beat.correlation.withId}`
      : `behaviour: ${beat.correlation.rule}`)
    : '';
  const quantity = [
    beat.stateKey ? `state: ${beat.stateKey}` : '',
    beat.unit ? `unit: ${beat.unit}` : '',
    Array.isArray(beat.range) ? `range: ${beat.range[0]}–${beat.range[1]}` : '',
  ].filter(Boolean).join('  ·  ');
  const subParts = (d && d.subParts && d.subParts.length)
    ? `<div style="margin-top:6px;"><span style="opacity:0.65;font-size:0.78rem;text-transform:uppercase;letter-spacing:0.04em;">Sub-parts</span><ul style="margin:2px 0 0 18px;padding:0;">${d.subParts.map((p) => `<li>${escapeHtml(p.name)}${p.purpose ? ` — ${escapeHtml(p.purpose)}` : ''}${p.appearance ? ` <span style="opacity:0.7;">(${escapeHtml(p.appearance)})</span>` : ''}</li>`).join('')}</ul></div>`
    : '';
  return `
    <div style="border:1px solid var(--border);border-radius:10px;padding:12px;margin-bottom:10px;">
      <div style="display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;">
        <strong>${escapeHtml(beat.label || beat.id)}</strong>
        <code style="opacity:0.6;font-size:0.78rem;">${escapeHtml(beat.id)}</code>
        ${beat.control ? `<span class="pill" style="font-size:0.72rem;">${escapeHtml(beat.control.kind)}: ${escapeHtml(beat.control.label)}</span>` : ''}
        ${!d ? '<span class="pill" style="font-size:0.72rem;opacity:0.75;">no detail spec</span>' : ''}
      </div>
      ${beat.description ? `<div style="margin-top:4px;">${escapeHtml(beat.description)}</div>` : ''}
      ${quantity ? `<div style="margin-top:4px;opacity:0.75;font-size:0.82rem;">${escapeHtml(quantity)}</div>` : ''}
      ${depLabels ? specFieldRow('Depends on', `${depLabels}${beat.mechanism ? ` — via ${beat.mechanism}` : ''}`) : specFieldRow('Role in chain', 'Root component (driven by the learner)')}
      ${corr ? specFieldRow('Correlation', corr) : ''}
      ${d ? specFieldRow('Role', d.role) : ''}
      ${d ? specFieldRow('Appearance', d.appearance) : ''}
      ${d ? specFieldRow('Graphics & motion', d.graphics) : ''}
      ${d ? specFieldRow('Behaviour', d.behavior) : ''}
      ${d ? specFieldRow('Calculations', d.calculations) : ''}
      ${d ? specFieldRow('Approximations', d.approximations) : ''}
      ${subParts}
    </div>
  `;
}

function showSpecOverlay(container, version, record) {
  const beats = record.beats || [];
  const meta = record.meta || null;
  const planner = meta && meta.planner;
  const critique = meta && meta.critique;
  const provenance = [
    planner && planner.model ? `Planned by ${planner.provider || '?'} / ${planner.model}${planner.reasoningEffort ? ` (${planner.reasoningEffort})` : ''}` : 'Planner model not recorded',
    meta && meta.completenessMode ? `Mode: ${meta.completenessMode}` : '',
    planner ? (planner.detailed ? 'Detail specs: yes' : 'Detail specs: none') : '',
    critique && critique.ran ? (critique.revised ? `Critic revised the plan — ${critique.reason || 'defect corrected'}` : 'Critic reviewed and approved') : 'Critic did not run',
    record.savedAt ? `Saved ${new Date(record.savedAt).toLocaleString()}` : '',
  ].filter(Boolean);

  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:9999;display:flex;align-items:center;justify-content:center;padding:24px;';
  overlay.innerHTML = `
    <div style="background:var(--surface,#14161b);color:var(--text,#e8eaf0);border:1px solid var(--border,#2a2f3a);border-radius:14px;max-width:860px;width:100%;max-height:86vh;display:flex;flex-direction:column;overflow:hidden;">
      <div style="padding:14px 18px;border-bottom:1px solid var(--border,#2a2f3a);display:flex;align-items:center;gap:12px;">
        <strong style="flex:1;">Component specification — version ${version}</strong>
        <button class="btn btn-sm" data-spec-copy>Copy JSON</button>
        <button class="btn btn-sm" data-spec-close>Close</button>
      </div>
      <div style="padding:10px 18px;border-bottom:1px solid var(--border,#2a2f3a);font-size:0.82rem;opacity:0.8;">
        ${provenance.map((p) => escapeHtml(p)).join(' &nbsp;·&nbsp; ')}
      </div>
      <div style="padding:16px 18px;overflow:auto;">
        <p style="margin:0 0 12px;opacity:0.75;font-size:0.85rem;">This is what the page was told to build. If a part is wrong on screen but correct here, the build failed. If it is wrong or missing here, the plan failed — and that is a planner-model or schema problem, not a code problem.</p>
        ${beats.map((b) => specBeatCard(b, beats)).join('') || '<p>No components recorded.</p>'}
      </div>
    </div>
  `;
  const close = () => overlay.remove();
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('[data-spec-close]').addEventListener('click', close);
  overlay.querySelector('[data-spec-copy]').addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(record, null, 2));
      overlay.querySelector('[data-spec-copy]').textContent = 'Copied ✓';
    } catch (_) { /* clipboard blocked — the panel itself is still readable */ }
  });
  document.addEventListener('keydown', function onEsc(e) {
    if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onEsc); }
  });
  (container.ownerDocument || document).body.appendChild(overlay);
}

function formatEstimateSeconds(seconds) {
  if (seconds < 90) return `~${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.round(seconds % 60);
  return `~${minutes}m ${remainder}s`;
}

// Same shape as formatEstimateSeconds, but for an actual measured duration
// (no "~") — mirrors functions/pluginAgentHandler.js's formatDuration exactly,
// so the live in-session bubble and the persisted chat message never
// disagree about how a given durationMs is worded.
function formatDurationLabel(ms) {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  if (totalSeconds < 90) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
}
